import {
  Activity,
  ArrowLeft,
  BarChart3,
  Bell,
  CalendarDays,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Mail,
  MapPin,
  Phone,
  Plus,
  Search,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Trash2,
  UserCircle,
  UserPlus,
  Users
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { api, ClassItem, ClassTemplate, CoachCoverage, formatAud, HolidayProgram, HolidayProgramBlock, Registration, School, StaffMember, Student, Term, Venue } from "./lib/api";

type Tab = "account" | "courses" | "students" | "my-account" | "cart" | "admin" | "add-student" | "edit-student";
type Lang = "en" | "zh";
type CourseLevel = "Beginner" | "Beginner to Intermediate" | "Intermediate" | "Advanced";

interface PublicData {
  school: School;
  venues: Venue[];
  terms: Term[];
}

interface GuardianProfile {
  userKey?: string;
  phone?: string;
  name?: string;
  email?: string;
}

interface StaffProfile {
  userKey?: string;
  phone?: string;
  email?: string;
  name?: string;
  role: "admin" | "coach" | "finance";
}

interface SessionProfile {
  userKey?: string;
  phone?: string;
  email?: string;
  role: "guardian" | "admin" | "coach" | "finance";
  guardian?: GuardianProfile;
  staff?: StaffProfile;
}

interface CheckoutResult {
  paymentConfigured: boolean;
  checkoutUrl?: string;
  message?: string;
  paymentContactEmail?: string;
  paymentInstructions?: string;
}

interface PaymentLinkRegistrationSummary {
  id: string;
  studentName: string;
  itemName: string;
  detail?: string;
  amountCents: number;
  paymentStatus: string;
}

interface PaymentLinkSummary {
  paymentConfigured: boolean;
  paymentContactEmail?: string;
  paymentInstructions?: string;
  paymentLink: {
    token: string;
    guardianEmail: string;
    expiresAt: string;
  };
  registrations: PaymentLinkRegistrationSummary[];
  totalCents: number;
  isPaid: boolean;
}

interface PaymentSettingsResponse {
  payment: {
    stripeConfigured: boolean;
    stripeConnectReady: boolean;
    stripeConnectAccountId: string;
    paymentInstructions?: string;
    paymentContactEmail?: string;
  };
  emailTemplates: {
    "unpaid-reminder": {
      id: string;
      subject: string;
      bodyText: string;
    };
  };
}

interface AttendanceReportStudent {
  registrationId: string;
  studentId: string;
  studentName: string;
  present: number;
  absent: number;
  leave: number;
  notMarked: number;
  attendanceRate: number | null;
  lastAbsence: string;
}

interface AttendanceReportClass {
  classId: string;
  className: string;
  termId: string;
  termName: string;
  coachKey: string;
  coachName: string;
  schedule: string;
  startsAt: string;
  expectedSessions: number;
  completedSessions: number;
  incompleteSessions: number;
  studentCount: number;
  present: number;
  absent: number;
  leave: number;
  attendanceRate: number | null;
  studentsAtRisk: number;
  students: AttendanceReportStudent[];
}

interface AttendanceReportResponse {
  filters: {
    termId: string;
    classId: string;
    coachKey: string;
    from: string;
    to: string;
    status: string;
  };
  totals: {
    classes: number;
    expectedSessions: number;
    completedSessions: number;
    incompleteSessions: number;
    students: number;
    present: number;
    absent: number;
    leave: number;
    attendanceRate: number | null;
  };
  classes: AttendanceReportClass[];
}

const copy = {
  en: {
    headline: "Badminton registration, payments and class management",
    subhead: "A mobile-first training school platform for fixed-term badminton classes across multiple venues.",
    account: "Student Registration",
    book: "Book a class",
    portal: "Portal",
    admin: "Admin",
    signIn: "Phone sign in",
    classes: "Available classes",
    courses: "Courses",
    register: "Register",
    waitlist: "Join waitlist",
    gst: "incl. GST",
    noClasses: "No classes are published for this school yet.",
    save: "Save",
    create: "Create",
    payment: "Payment",
    attendance: "Attendance",
    health: "Health details"
  },
  zh: {
    headline: "羽毛球学校报名、付款与课程管理",
    subhead: "适合多校区、固定 Term 班级的移动端优先培训学校系统。",
    account: "学生注册",
    book: "报名课程",
    portal: "我的课程",
    admin: "后台",
    signIn: "手机登录",
    classes: "可报名班级",
    courses: "课程",
    register: "报名",
    waitlist: "加入候补",
    gst: "含 GST",
    noClasses: "这个学校暂时还没有发布班级。",
    save: "保存",
    create: "创建",
    payment: "付款",
    attendance: "出勤",
    health: "健康信息"
  }
};

const courseLevels: CourseLevel[] = ["Beginner", "Beginner to Intermediate", "Intermediate", "Advanced"];

const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const weekdayAliases: Record<string, string[]> = {
  Monday: ["monday", "mon"],
  Tuesday: ["tuesday", "tue", "tues"],
  Wednesday: ["wednesday", "wed"],
  Thursday: ["thursday", "thu", "thur", "thurs"],
  Friday: ["friday", "fri"],
  Saturday: ["saturday", "sat"],
  Sunday: ["sunday", "sun"]
};

function normalizeCourseLevel(value?: string): CourseLevel {
  const level = (value ?? "").toLowerCase();
  const hasBeginner = level.includes("beginner");
  const hasIntermediate = level.includes("intermediate");

  if (level.includes("advanced") || level.includes("elite") || level.includes("performance")) return "Advanced";
  if (hasBeginner && hasIntermediate) return "Beginner to Intermediate";
  if (hasIntermediate || level.includes("development")) return "Intermediate";
  return "Beginner";
}

function getCourseLevelRank(value?: string) {
  return courseLevels.indexOf(normalizeCourseLevel(value));
}

function scheduleMatchesWeekday(schedule: string | undefined, weekday: string) {
  if (!weekday) return true;
  const normalizedSchedule = (schedule ?? "").toLowerCase();
  return (weekdayAliases[weekday] ?? []).some((alias) => normalizedSchedule.includes(alias));
}

function holidayBlockMatchesWeekday(block: HolidayProgramBlock, weekday: string) {
  if (!weekday) return true;
  const date = new Date(`${block.date}T00:00:00`);
  const weekdayName = weekdays[date.getDay() === 0 ? 6 : date.getDay() - 1];
  return weekdayName === weekday;
}

function blockTimeLabel(block: HolidayProgramBlock) {
  return `${block.startTime} - ${block.endTime}`;
}

function formatHolidayBlockDate(block: HolidayProgramBlock) {
  return new Date(`${block.date}T00:00:00`).toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric"
  });
}

function calculateAgeFromDateOfBirth(dateOfBirth?: string) {
  if (!dateOfBirth) return null;
  const [year, month, day] = dateOfBirth.split("-").map((value) => Number(value));
  if (!year || !month || !day) return null;
  const today = new Date();
  let age = today.getFullYear() - year;
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();
  if (currentMonth < month || (currentMonth === month && currentDay < day)) age -= 1;
  return age >= 0 && age < 120 ? age : null;
}

function studentAgeLabel(dateOfBirth?: string) {
  const age = calculateAgeFromDateOfBirth(dateOfBirth);
  return age === null ? "Not recorded" : String(age);
}

function studentAgeSentence(dateOfBirth?: string) {
  const age = calculateAgeFromDateOfBirth(dateOfBirth);
  return age === null ? "Age not recorded" : `Age ${age}`;
}

function DateOfBirthAgeFields({ defaultValue = "" }: { defaultValue?: string }) {
  const [dateOfBirth, setDateOfBirth] = useState(defaultValue);
  return (
    <div className="two-cols">
      <div>
        <label>Date of birth</label>
        <input name="dateOfBirth" type="date" defaultValue={defaultValue} required onChange={(event) => setDateOfBirth(event.target.value)} />
      </div>
      <div>
        <label>Age</label>
        <input className="readonly-input" value={studentAgeLabel(dateOfBirth)} readOnly />
      </div>
    </div>
  );
}

function groupHolidayBlocksByDate(blocks: HolidayProgramBlock[]) {
  const groups = new Map<string, HolidayProgramBlock[]>();
  blocks.forEach((block) => {
    groups.set(block.date, [...(groups.get(block.date) ?? []), block]);
  });
  return Array.from(groups.entries()).map(([date, dateBlocks]) => ({
    date,
    blocks: dateBlocks
  }));
}

function normalizeAustralianPhoneInput(value: string) {
  const compact = value.replace(/[\s()-]/g, "");
  if (compact.startsWith("04")) return `+61${compact.slice(1)}`;
  return compact;
}

const SCHOOL_SLUG_STORAGE_KEY = "relayro.schoolSlug";
const RESERVED_ENTRY_SEGMENTS = new Set(["portal", "account", "admin", "cart"]);

function resolveInitialSchoolSlug() {
  const segment = window.location.pathname.split("/").filter(Boolean)[0] ?? "";
  const storedSlug = localStorage.getItem(SCHOOL_SLUG_STORAGE_KEY) ?? "";
  if (segment && !RESERVED_ENTRY_SEGMENTS.has(segment)) {
    localStorage.setItem(SCHOOL_SLUG_STORAGE_KEY, segment);
    return segment;
  }
  if (storedSlug) {
    const nextPath = `/${storedSlug}${window.location.search}${window.location.hash}`;
    if (window.location.pathname !== `/${storedSlug}`) {
      window.history.replaceState(null, "", nextPath);
    }
    return storedSlug;
  }
  return "demo";
}

export function App() {
  const initialSlug = resolveInitialSchoolSlug();
  const schoolSlug = initialSlug;
  const paymentToken = useMemo(() => new URLSearchParams(window.location.search).get("pay") ?? "", []);
  const paymentResult = useMemo(() => new URLSearchParams(window.location.search).get("payment") ?? "", []);
  const stripeConnectReturn = useMemo(() => new URLSearchParams(window.location.search).get("stripe_connect") === "return", []);
  const [activeTab, setActiveTab] = useState<Tab>(stripeConnectReturn ? "admin" : "courses");
  const lang: Lang = "en";
  const [publicData, setPublicData] = useState<PublicData | null>(null);
	  const [classes, setClasses] = useState<ClassItem[]>([]);
	  const [holidayPrograms, setHolidayPrograms] = useState<HolidayProgram[]>([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isAuthed, setIsAuthed] = useState(Boolean(localStorage.getItem("relayro.session")));
	  const [showLogin, setShowLogin] = useState(false);
	  const [loginMode, setLoginMode] = useState<"guardian" | "staff">("guardian");
  const [guardian, setGuardian] = useState<GuardianProfile | null>(null);
  const [sessionProfile, setSessionProfile] = useState<SessionProfile | null>(null);
  const [cartItems, setCartItems] = useState<Registration[]>([]);
  const [cartAutoCheckout, setCartAutoCheckout] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  const t = copy[lang];
  const isStaff = Boolean(sessionProfile && sessionProfile.role !== "guardian");

  useEffect(() => {
    loadPublic();
  }, [schoolSlug]);

	  useEffect(() => {
	    if (isAuthed) {
	      void loadSessionSummary();
	      if (activeTab === "account") setActiveTab("courses");
	    } else {
	      setGuardian(null);
      setSessionProfile(null);
      setCartItems([]);
      setCartAutoCheckout(false);
	    }
		  }, [isAuthed, schoolSlug]);

	  useEffect(() => {
	    if (isAuthed && sessionProfile?.role === "guardian" && activeTab === "admin") {
	      setActiveTab("students");
	    }
	  }, [activeTab, isAuthed, sessionProfile?.role]);

  async function loadPublic() {
    setError("");
    setNotice("");
    try {
      const school = await api<PublicData>(`/public/schools/${schoolSlug}`);
	      const [classData, holidayProgramData] = await Promise.all([
	        api<{ classes: ClassItem[] }>(`/public/schools/${schoolSlug}/classes`),
	        api<{ programs: HolidayProgram[] }>(`/public/schools/${schoolSlug}/holiday-programs`)
	      ]);
	      setPublicData(school);
	      setClasses(classData.classes);
	      setHolidayPrograms(holidayProgramData.programs);
	    } catch (err) {
	      setPublicData(null);
	      setClasses([]);
	      setHolidayPrograms([]);
      setError(err instanceof Error ? err.message : "Unable to load school");
    }
  }

  async function loadSessionSummary() {
    try {
      const sessionResult = await api<SessionProfile>(`/portal/schools/${schoolSlug}/session`);
      setSessionProfile(sessionResult);
      if (sessionResult.role !== "guardian") {
        setGuardian({ userKey: sessionResult.userKey, phone: sessionResult.phone, name: sessionResult.staff?.name, email: sessionResult.email ?? sessionResult.staff?.email ?? "" });
        setCartItems([]);
        setCartAutoCheckout(false);
        return;
      }
      const [studentResult, registrationResult] = await Promise.all([
        api<{ guardian?: GuardianProfile; students: Student[] }>(`/portal/schools/${schoolSlug}/students`),
        api<{ registrations: Registration[] }>(`/portal/schools/${schoolSlug}/registrations`)
      ]);
      setGuardian(studentResult.guardian ?? null);
      setCartItems(registrationResult.registrations.filter((registration) => registration.status !== "cancelled" && registration.paymentStatus !== "paid"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load account summary");
    }
  }

  function logout() {
    localStorage.removeItem("relayro.session");
    setIsAuthed(false);
    setActiveTab("courses");
    setNotice("Signed out.");
  }

	function openCart(autoCheckout = true, registration?: Registration) {
    if (isStaff) return;
			if (registration && registration.status !== "cancelled" && registration.paymentStatus !== "paid") {
				setCartItems((current) => (current.some((item) => item.id === registration.id) ? current : [registration, ...current]));
    }
    setActiveTab("cart");
		setCartAutoCheckout(autoCheckout);
	}

			async function finishAddStudent() {
      if (isStaff) return;
				await loadSessionSummary();
				setActiveTab("students");
			}

  const theme = useMemo(
    () => ({
      "--brand": publicData?.school.brandColor || "#111111",
      "--accent": publicData?.school.accentColor || "#c1a576"
    }),
    [publicData]
  );

  return (
    <main style={theme as React.CSSProperties}>
      <header className="site-header">
        <a className="brand" href={`/${schoolSlug}`}>
          <span className="brand-mark">
            {publicData?.school.logoUrl ? <img src={publicData.school.logoUrl} alt="" /> : <Activity size={24} />}
          </span>
          <span>{publicData?.school.name ?? "Relayro Badminton"}</span>
        </a>
        <nav className="top-nav" aria-label="Primary">
	          {isAuthed ? (
	            <>
	              <button className="account-button" onClick={() => setActiveTab(isStaff ? "my-account" : "students")}>
	                <UserCircle size={18} />
	                {sessionProfile?.staff?.name || guardian?.name || sessionProfile?.email || guardian?.email || sessionProfile?.phone || guardian?.phone || "My Account"}
	              </button>
              {!isStaff ? (
                <button className="cart-button" onClick={() => openCart(true)} aria-label={`Cart with ${cartItems.length} unpaid items`}>
                  <ShoppingCart size={18} />
                  Cart
                  {cartItems.length > 0 ? <span className="cart-badge">{cartItems.length}</span> : null}
                </button>
              ) : null}
              <button className="logout-button" onClick={logout}>
                <LogOut size={18} />
                Logout
              </button>
              {isStaff ? (
              <button className={activeTab === "admin" ? "active" : ""} onClick={() => setActiveTab("admin")}>
                  <LayoutDashboard size={18} />
                  {sessionProfile?.role === "admin" ? "Business settings" : sessionProfile?.role === "finance" ? "Payments" : "Coach roster"}
                </button>
              ) : null}
            </>
          ) : (
            <button
	              className="signin-button"
	              onClick={() => {
	                setLoginMode("guardian");
		                setActiveTab("students");
		                if (!isAuthed) setShowLogin(true);
		              }}
            >
              <Smartphone size={18} />
              Sign in
            </button>
          )}
          {!isAuthed ? (
            <button
              className={activeTab === "admin" ? "active" : ""}
	              onClick={() => {
	                setLoginMode("staff");
	                setActiveTab("admin");
	                setShowLogin(true);
	              }}
            >
              <LayoutDashboard size={18} />
              Staff Login
            </button>
          ) : null}
        </nav>
      </header>

	      {!isAuthed && !paymentToken ? <section className="hero">
	        <div>
	          <p className="eyebrow">Relayro SaaS for badminton schools</p>
	          <h1>{t.headline}</h1>
          <p>{t.subhead}</p>
          <div className="hero-actions">
            <button className="primary" onClick={() => setActiveTab("courses")}>
              {t.courses}
            </button>
            <button className="secondary" onClick={() => setActiveTab("account")}>
              {t.account}
            </button>
	          </div>
	        </div>
	      </section> : null}

	      {error ? (
	        <section className="alert error">
	          <strong>{error}</strong>
	        </section>
	      ) : null}
	      {notice ? <section className="alert success">{notice}</section> : null}

	      {paymentToken ? (
	        <PaymentLinkView
	          schoolSlug={schoolSlug}
	          token={paymentToken}
	          paymentResult={paymentResult}
	          setError={setError}
	          setNotice={setNotice}
	        />
	      ) : null}

	      {!paymentToken && isAuthed && !isStaff && ["students", "courses", "my-account", "add-student", "edit-student", "cart"].includes(activeTab) ? (
	        <ParentTabs activeTab={activeTab} setActiveTab={setActiveTab} />
	      ) : null}

	      {!paymentToken && activeTab === "account" ? (
		        <AccountView
		          schoolSlug={schoolSlug}
		          isAuthed={isAuthed}
	          setIsAuthed={setIsAuthed}
	          setNotice={setNotice}
	          setError={setError}
	          onCartChanged={loadSessionSummary}
		          openCart={() => openCart(true)}
		          openAddStudent={() => setActiveTab("add-student")}
              sessionProfile={sessionProfile}
		        />
	      ) : null}
	      {!paymentToken && activeTab === "courses" ? (
	        <CourseRegistrationView
          t={t}
          schoolSlug={schoolSlug}
          publicData={publicData}
          classes={classes}
          holidayPrograms={holidayPrograms}
          isAuthed={isAuthed}
	          setIsAuthed={setIsAuthed}
            isStaff={isStaff}
	          openLogin={() => setShowLogin(true)}
          setNotice={setNotice}
          setError={setError}
	          onCartChanged={loadSessionSummary}
	          goToCart={(registration) => openCart(true, registration)}
		          openAddStudent={() => setActiveTab("add-student")}
		        />
		      ) : null}
		      {!paymentToken && activeTab === "students" ? (
	        <StudentsView
	          t={t}
	          schoolSlug={schoolSlug}
	          classes={classes}
	          holidayPrograms={holidayPrograms}
	          setError={setError}
	          setNotice={setNotice}
	          isAuthed={isAuthed}
		          openLogin={() => setShowLogin(true)}
		          onCartChanged={loadSessionSummary}
		          openCart={() => openCart(true)}
			          openAddStudent={() => setActiveTab("add-student")}
		          editStudent={(student) => {
		            setSelectedStudent(student);
		            setActiveTab("edit-student");
		          }}
		        />
		      ) : null}
		      {!paymentToken && activeTab === "my-account" ? (
		        <MyAccountView
		          schoolSlug={schoolSlug}
		          isAuthed={isAuthed}
		          sessionProfile={sessionProfile}
		          setError={setError}
		          setNotice={setNotice}
		          onSaved={loadSessionSummary}
		          openLogin={() => setShowLogin(true)}
		        />
		      ) : null}
		      {!paymentToken && activeTab === "add-student" && !isStaff ? (
		        <AddStudentView
		          schoolSlug={schoolSlug}
		          setNotice={setNotice}
		          setError={setError}
		          onSaved={finishAddStudent}
		          onCancel={() => setActiveTab("students")}
		        />
		      ) : null}
		      {!paymentToken && activeTab === "edit-student" && selectedStudent ? (
		        <EditStudentView
		          schoolSlug={schoolSlug}
		          student={selectedStudent}
		          setNotice={setNotice}
		          setError={setError}
		          onSaved={async () => {
		            setSelectedStudent(null);
		            await loadSessionSummary();
		            setActiveTab("students");
		          }}
		          onCancel={() => {
		            setSelectedStudent(null);
		            setActiveTab("students");
		          }}
		        />
		      ) : null}
      {!paymentToken && activeTab === "cart" ? (
        <CartView
          schoolSlug={schoolSlug}
	          registrations={cartItems}
	          classes={classes}
	          holidayPrograms={holidayPrograms}
          autoStartCheckout={cartAutoCheckout}
          onAutoStartHandled={() => setCartAutoCheckout(false)}
          onReload={loadSessionSummary}
          setError={setError}
          setNotice={setNotice}
        />
      ) : null}
      {!paymentToken && activeTab === "admin" ? (
          <AdminView
            t={t}
            schoolSlug={schoolSlug}
            classes={classes}
            terms={publicData?.terms ?? []}
            holidayPrograms={holidayPrograms}
	          refresh={loadPublic}
	          setError={setError}
	          setNotice={setNotice}
	          isAuthed={isAuthed}
	          setIsAuthed={setIsAuthed}
	          isStaff={isStaff}
	          staffRole={sessionProfile?.role}
            sessionProfile={sessionProfile}
            initialCollection={stripeConnectReturn ? "payments-emails" : undefined}
		          openLogin={() => setShowLogin(true)}
            />
      ) : null}
      {showLogin ? (
	        <LoginModal
	          schoolSlug={schoolSlug}
	          mode={loginMode}
	          onClose={() => setShowLogin(false)}
	          onAuthed={() => {
	            setIsAuthed(true);
	            setShowLogin(false);
	            void loadSessionSummary();
	          }}
	          onCreateAccount={() => {
	            setShowLogin(false);
	            setActiveTab("account");
	          }}
	          setIsAuthed={setIsAuthed}
	          setError={setError}
	        />
      ) : null}
    </main>
	  );
	}

function ParentTabs({ activeTab, setActiveTab }: { activeTab: Tab; setActiveTab: (tab: Tab) => void }) {
  const tabs: Array<{ id: Tab; label: string; mobileLabel?: string }> = [
    { id: "students", label: "Students" },
    { id: "courses", label: "Courses" },
    { id: "my-account", label: "My Account", mobileLabel: "Account" }
  ];

  return (
    <nav className="parent-tabs" aria-label="Guardian sections">
      {tabs.map((tab) => (
        <button
          aria-current={activeTab === tab.id ? "page" : undefined}
          className={activeTab === tab.id ? "active" : ""}
          type="button"
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
        >
          <span className="tab-label-full">{tab.label}</span>
          <span className="tab-label-mobile">{tab.mobileLabel ?? tab.label}</span>
        </button>
      ))}
    </nav>
  );
}

function LoginPanel({
  schoolSlug,
  mode = "guardian",
  onAuthed,
  setIsAuthed,
  setError
}: {
  schoolSlug: string;
  mode?: "guardian" | "staff";
  onAuthed?: () => void;
  setIsAuthed?: (value: boolean) => void;
  setError?: (message: string) => void;
}) {
  const [method, setMethod] = useState<"email" | "phone">("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");

  async function start() {
    setMessage("");
    if (method === "email") {
      await api("/auth/email/start", { method: "POST", body: JSON.stringify({ email }) });
      setMessage("Code sent by email.");
      return;
    }
    await api("/auth/start", { method: "POST", body: JSON.stringify({ phone: normalizeAustralianPhoneInput(phone) }) });
    setMessage("Code sent by SMS.");
  }

		  async function verify() {
		    const result = await api<{ token: string }>(method === "email" ? "/auth/email/verify" : "/auth/verify", {
		      method: "POST",
		      body: JSON.stringify(method === "email" ? { email, code } : { phone: normalizeAustralianPhoneInput(phone), code })
		    });
		    localStorage.setItem("relayro.session", result.token);
		    if (mode === "staff") {
		      const session = await api<SessionProfile>(`/portal/schools/${schoolSlug}/session`);
	      if (session.role === "guardian") {
	        localStorage.removeItem("relayro.session");
	        setIsAuthed?.(false);
	        const message = "This phone number is not a staff account. Please use the admin, coach or finance staff phone.";
	        setMessage(message);
	        setError?.(message);
	        return;
	      }
	    }
	    setIsAuthed?.(true);
	    setMessage("Signed in.");
	    onAuthed?.();
		  }

						  return (
		    <section className="panel compact">
		      <div className="panel-title">
		        {method === "email" ? <Mail size={20} /> : <Smartphone size={20} />}
		        <h2>{mode === "staff" ? "Staff sign in" : "Sign in"}</h2>
		      </div>
      <div className="segmented" role="tablist" aria-label="Sign in method">
        <button type="button" className={method === "email" ? "active" : ""} onClick={() => setMethod("email")}>
          Email
        </button>
        <button type="button" className={method === "phone" ? "active" : ""} onClick={() => setMethod("phone")}>
          Phone
        </button>
      </div>
      {method === "email" ? (
        <>
          <label>Email</label>
          <input type="email" placeholder="parent@example.com" value={email} onChange={(event) => setEmail(event.target.value)} />
        </>
      ) : (
        <>
	        <label>Australian mobile number</label>
	        <input type="tel" inputMode="tel" placeholder="0412345678" value={phone} onChange={(event) => setPhone(event.target.value)} />
        </>
      )}
	      <div className="inline">
	        <input placeholder={method === "email" ? "Email code" : "SMS code"} value={code} onChange={(event) => setCode(event.target.value)} />
	        <button onClick={start}>Send</button>
	        <button className="primary" onClick={verify}>
	          Verify
        </button>
      </div>
      {message ? <p className="muted">{message}</p> : null}
    </section>
  );
}

function LoginModal({
  schoolSlug,
  mode,
  onClose,
  onAuthed,
  onCreateAccount,
  setIsAuthed,
  setError
}: {
  schoolSlug: string;
  mode: "guardian" | "staff";
  onClose: () => void;
  onAuthed: () => void;
  onCreateAccount: () => void;
  setIsAuthed: (value: boolean) => void;
  setError: (message: string) => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section className="login-modal" role="dialog" aria-modal="true" aria-labelledby="login-title" onClick={(event) => event.stopPropagation()}>
	        <div className="modal-header">
	          <h2 id="login-title">{mode === "staff" ? "Staff Login" : "Sign in"}</h2>
          <button className="icon-button" aria-label="Close sign in" onClick={onClose}>
            ×
          </button>
        </div>
	        <LoginPanel
	          schoolSlug={schoolSlug}
	          mode={mode}
		          setIsAuthed={setIsAuthed}
		          setError={setError}
	          onAuthed={() => {
	            setIsAuthed(true);
	            onAuthed();
	          }}
	        />
	        {mode === "guardian" ? (
	          <button className="secondary full-width" type="button" onClick={onCreateAccount}>
	            Create New Account
	          </button>
	        ) : null}
	      </section>
	    </div>
	  );
}

function AccountView({
  schoolSlug,
  isAuthed,
  setIsAuthed,
	setNotice,
	setError,
	onCartChanged,
	openCart,
	openAddStudent,
	sessionProfile
}: {
	schoolSlug: string;
	isAuthed: boolean;
	setIsAuthed: (value: boolean) => void;
	setNotice: (message: string) => void;
	setError: (message: string) => void;
	onCartChanged: () => Promise<void>;
	openCart: () => void;
	openAddStudent: () => void;
  sessionProfile: SessionProfile | null;
	}) {
  if (isAuthed) {
    return null;
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const result = await api<{ token: string }>(`/public/schools/${schoolSlug}/accounts`, {
        method: "POST",
        body: JSON.stringify({
          phone: normalizeAustralianPhoneInput(String(form.get("phone"))),
          guardianEmail: String(form.get("guardianEmail")),
          parentName: String(form.get("parentName")),
          studentName: String(form.get("studentName")),
          dateOfBirth: String(form.get("dateOfBirth")),
          allergies: String(form.get("allergies")),
          healthNotes: String(form.get("healthNotes")),
          emergencyName: String(form.get("emergencyName")),
          emergencyPhone: String(form.get("emergencyPhone"))
        })
      });
      localStorage.setItem("relayro.session", result.token);
      setIsAuthed(true);
      event.currentTarget.reset();
      setNotice("Account and student profile created. You can now select a course.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to create account");
    }
  }

  return (
    <div className="page-grid">
      <section>
        <div className="section-heading">
          <h2>Student Registration</h2>
          <p>Create a guardian account and add the first student profile. Course registration happens after sign in.</p>
        </div>
        <form className="panel wide-form" onSubmit={submit}>
          <div className="panel-title">
            <ShieldCheck size={20} />
            <h2>Guardian account</h2>
          </div>
          <div className="two-cols">
            <div>
              <label>Australian mobile number</label>
              <input name="phone" type="tel" inputMode="tel" placeholder="0412345678" required />
            </div>
            <div>
              <label>Email</label>
              <input name="guardianEmail" type="email" required />
            </div>
          </div>
          <label>Parent / guardian name</label>
          <input name="parentName" required />
          <div className="panel-title form-section-title">
            <Users size={20} />
            <h2>Student profile</h2>
          </div>
          <label>Student name</label>
          <input name="studentName" required />
          <DateOfBirthAgeFields />
          <label>Allergies</label>
          <textarea name="allergies" rows={2} />
          <label>Health conditions</label>
          <textarea name="healthNotes" rows={2} />
          <div className="two-cols">
            <div>
              <label>Emergency contact</label>
              <input name="emergencyName" required />
            </div>
            <div>
              <label>Emergency phone</label>
              <input name="emergencyPhone" type="tel" required />
            </div>
          </div>
          <button className="primary" type="submit">
            Create account
          </button>
        </form>
	      </section>
	      <aside>
	        <section className="panel compact">
	          <div className="panel-title">
	            <Smartphone size={20} />
	            <h2>Already registered?</h2>
	          </div>
	          <p className="muted">Use the Sign in button in the top right to access your students and course registrations.</p>
	        </section>
	      </aside>
    </div>
  );
}

function CourseRegistrationView({
  t,
  schoolSlug,
	  publicData,
	  classes,
	  holidayPrograms,
		isAuthed,
	setIsAuthed,
  isStaff,
	openLogin,
  setNotice,
	setError,
	onCartChanged,
	goToCart,
	openAddStudent
}: {
  t: (typeof copy)["en"];
  schoolSlug: string;
	  publicData: PublicData | null;
	  classes: ClassItem[];
	  holidayPrograms: HolidayProgram[];
	isAuthed: boolean;
	setIsAuthed: (value: boolean) => void;
  isStaff: boolean;
	openLogin: () => void;
  setNotice: (message: string) => void;
	setError: (message: string) => void;
	onCartChanged: () => Promise<void>;
	goToCart: (registration?: Registration) => void;
	openAddStudent: () => void;
}) {
	  const [step, setStep] = useState<"browse" | "confirm">("browse");
	  const [programType, setProgramType] = useState<"classes" | "holiday">("classes");
	  const [selectedClass, setSelectedClass] = useState<ClassItem | null>(null);
	  const [selectedProgram, setSelectedProgram] = useState<HolidayProgram | null>(null);
	  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [location, setLocation] = useState("");
  const [level, setLevel] = useState("");
  const [time, setTime] = useState("");

  useEffect(() => {
    if (isAuthed) void loadStudents();
  }, [isAuthed, schoolSlug]);

		  useEffect(() => {
	    if (isAuthed && (selectedClass || selectedProgram) && step === "browse") setStep("confirm");
	  }, [isAuthed, selectedClass, selectedProgram, step]);

  async function loadStudents() {
    try {
      const result = await api<{ students: Student[] }>(`/portal/schools/${schoolSlug}/students`);
      setStudents(result.students);
      setSelectedStudentId((current) => current || result.students[0]?.id || "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load students");
    }
  }

	  const filteredClasses = useMemo(
    () =>
      [...classes]
        .sort((a, b) => {
          const levelDiff = getCourseLevelRank(a.level) - getCourseLevelRank(b.level);
          if (levelDiff !== 0) return levelDiff;
          return `${a.schedule} ${a.name}`.localeCompare(`${b.schedule} ${b.name}`);
        })
        .filter((klass) => {
          const matchesLocation = !location || klass.venueId === location || klass.venueName === location;
          const matchesLevel = !level || normalizeCourseLevel(klass.level) === level;
          const matchesTime = scheduleMatchesWeekday(klass.schedule, time);
          return matchesLocation && matchesLevel && matchesTime;
        }),
	    [classes, location, level, time]
	  );

	  const filteredHolidayPrograms = useMemo(
	    () =>
	      [...holidayPrograms]
	        .sort((a, b) => {
	          const levelDiff = getCourseLevelRank(a.level) - getCourseLevelRank(b.level);
	          if (levelDiff !== 0) return levelDiff;
	          return `${a.startsAt} ${a.name}`.localeCompare(`${b.startsAt} ${b.name}`);
	        })
	        .filter((program) => {
	          const matchesLocation = !location || program.venueId === location || program.venueName === location;
	          const matchesLevel = !level || normalizeCourseLevel(program.level) === level;
	          const matchesTime = !time || (program.blocks ?? []).some((block) => holidayBlockMatchesWeekday(block, time));
	          return matchesLocation && matchesLevel && matchesTime;
	        }),
	    [holidayPrograms, location, level, time]
	  );

	  const selectedProgramBlocks = useMemo(() => {
	    if (!selectedProgram) return [];
	    return selectedProgram.blocks.filter((block) => !time || holidayBlockMatchesWeekday(block, time));
	  }, [selectedProgram, time]);

	  const selectedHolidayTotalCents = useMemo(() => {
	    if (!selectedProgram) return 0;
	    return selectedProgram.blocks
	      .filter((block) => selectedBlockIds.includes(block.id))
	      .reduce((sum, block) => sum + Number(block.priceCents ?? selectedProgram.blockPriceCents ?? 0), 0);
	  }, [selectedBlockIds, selectedProgram]);

	  async function registerForClass() {
	    if (!selectedClass) return;
    if (!selectedStudentId) {
      setError("Please select a student before registering.");
      return;
    }
    try {
      const result = await api<{ registration: Registration }>(`/public/schools/${schoolSlug}/registrations`, {
        method: "POST",
        body: JSON.stringify({
          classId: selectedClass.id,
          studentId: selectedStudentId
        })
      });
      setNotice("Course registration added to cart.");
      await onCartChanged();
      setStep("browse");
      setSelectedClass(null);
      goToCart(result.registration);
	    } catch (err) {
	      setError(err instanceof Error ? err.message : "Unable to register for class");
		    }
		  }

		  async function registerForHolidayProgram() {
		    if (!selectedProgram) return;
		    if (!selectedStudentId) {
		      setError("Please select a student before registering.");
		      return;
		    }
		    if (selectedBlockIds.length === 0) {
		      setError("Please select at least one holiday program block.");
		      return;
		    }
		    try {
		      const result = await api<{ registration: Registration }>(`/public/schools/${schoolSlug}/registrations`, {
		        method: "POST",
		        body: JSON.stringify({
		          programId: selectedProgram.id,
		          studentId: selectedStudentId,
		          blockIds: selectedBlockIds
		        })
		      });
		      setNotice("Holiday program registration added to cart.");
		      await onCartChanged();
		      setStep("browse");
		      setSelectedProgram(null);
		      setSelectedBlockIds([]);
		      goToCart(result.registration);
		    } catch (err) {
		      setError(err instanceof Error ? err.message : "Unable to register for holiday program");
		    }
		  }

		  function toggleBlock(blockId: string) {
		    setSelectedBlockIds((current) => (current.includes(blockId) ? current.filter((id) => id !== blockId) : [...current, blockId]));
		  }

				  return (
    <div className="page-grid">
      <section>
        {step === "browse" ? (
          <>
            <div className="section-heading">
              <h2>{t.classes}</h2>
              <p>Step 1 of 2: filter courses by location, level and day, then choose a class.</p>
            </div>
            <div className="filter-bar course-filter-bar">
              <div>
                <label>Program type</label>
                <select
                  value={programType}
                  onChange={(event) => {
                    setProgramType(event.target.value as "classes" | "holiday");
                    setSelectedClass(null);
                    setSelectedProgram(null);
                    setSelectedBlockIds([]);
                  }}
                >
                  <option value="classes">Classes</option>
                  <option value="holiday">Holiday Programs</option>
                </select>
              </div>
              <div>
                <label>Location</label>
                <select value={location} onChange={(event) => setLocation(event.target.value)}>
                  <option value="">All locations</option>
                  {(publicData?.venues ?? []).map((venue) => (
                    <option value={venue.id} key={venue.id}>
                      {venue.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>Level</label>
                <select value={level} onChange={(event) => setLevel(event.target.value)}>
                  <option value="">All levels</option>
                  {courseLevels.map((courseLevel) => (
                    <option value={courseLevel} key={courseLevel}>
                      {courseLevel}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>Time</label>
                <select value={time} onChange={(event) => setTime(event.target.value)}>
                  <option value="">Any day</option>
                  {weekdays.map((weekday) => (
                    <option value={weekday} key={weekday}>
                      {weekday}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="class-grid">
              {programType === "classes" ? (
                <>
                  {filteredClasses.length === 0 ? <p className="muted">{t.noClasses}</p> : null}
                  {filteredClasses.map((klass) => (
                    <ClassCard
                      key={klass.id}
                      klass={klass}
                      t={t}
                      onRegister={() => {
                        if (isStaff) {
                          setError("Staff accounts cannot register for courses. Please log out and sign in as a guardian.");
                          return;
                        }
                        setSelectedClass(klass);
                        setSelectedProgram(null);
                        if (!isAuthed) {
                          openLogin();
                          return;
                        }
                        setStep("confirm");
                      }}
                    />
                  ))}
                </>
              ) : (
                <>
                  {filteredHolidayPrograms.length === 0 ? <p className="muted">No holiday programs are published for this school yet.</p> : null}
                  {filteredHolidayPrograms.map((program) => (
                    <HolidayProgramCard
                      key={program.id}
                      program={program}
                      onRegister={() => {
                        if (isStaff) {
                          setError("Staff accounts cannot register for courses. Please log out and sign in as a guardian.");
                          return;
                        }
                        setSelectedProgram(program);
                        setSelectedClass(null);
                        setSelectedBlockIds([]);
                        if (!isAuthed) {
                          openLogin();
                          return;
                        }
                        setStep("confirm");
                      }}
                    />
                  ))}
                </>
              )}
            </div>
          </>
        ) : (
          <section className="panel confirm-panel">
            <div className="panel-title">
              <Search size={20} />
              <h2>Confirm registration</h2>
            </div>
	            <p className="muted">Step 2 of 2: choose the student and confirm the selection.</p>
	            {selectedClass ? <ClassCard klass={selectedClass} t={t} /> : null}
	            {selectedProgram ? <HolidayProgramCard program={selectedProgram} /> : null}
            {!isAuthed ? (
              <div className="alert error inline-alert">
                <strong>Please sign in before confirming a course registration.</strong>
                <button className="primary" type="button" onClick={openLogin}>
                  Sign in
                </button>
              </div>
            ) : (
              <>
                <label>Student</label>
                <select value={selectedStudentId} onChange={(event) => setSelectedStudentId(event.target.value)}>
                  <option value="">Select student</option>
                  {students.map((student) => (
                    <option value={student.id} key={student.id}>
                      {student.name}
                    </option>
                  ))}
	                </select>
	                {selectedProgram ? (
	                  <section className="holiday-block-panel">
	                    <div className="cart-total">
	                      <span>{selectedBlockIds.length} block{selectedBlockIds.length === 1 ? "" : "s"} selected</span>
	                      <strong>{formatAud(selectedHolidayTotalCents)}</strong>
	                    </div>
	                    <div className="holiday-block-list">
	                      {selectedProgramBlocks.map((block) => {
	                        const isFull = Number(block.bookedCount ?? 0) >= Number(block.capacity ?? 0);
	                        const isClosed = isFull || block.open === false;
	                        return (
	                          <label className={`holiday-block-option ${isClosed ? "disabled" : ""}`} key={block.id}>
	                            <input
	                              type="checkbox"
	                              checked={selectedBlockIds.includes(block.id)}
	                              disabled={isClosed}
	                              onChange={() => toggleBlock(block.id)}
	                            />
	                            <span>
	                              <strong>{new Date(`${block.date}T00:00:00`).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "short", year: "numeric" })}</strong>
	                              {blockTimeLabel(block)}
	                            </span>
	                            <span>{isFull ? "No openings available" : `${Number(block.capacity ?? 0) - Number(block.bookedCount ?? 0)} spots`}</span>
	                            <span>{formatAud(Number(block.priceCents ?? selectedProgram.blockPriceCents ?? 0))}</span>
	                          </label>
	                        );
	                      })}
	                    </div>
	                  </section>
	                ) : null}
		                {students.length === 0 ? (
	                  <div className="empty-action">
	                    <p className="muted">No student profiles yet. Add a student before registering for a course.</p>
	                    <button className="primary" type="button" onClick={openAddStudent}>
	                      <UserPlus size={16} />
	                      Add Student
	                    </button>
	                  </div>
	                ) : null}
                <div className="inline form-actions">
                  <button type="button" onClick={() => setStep("browse")}>
                    Back
                  </button>
	                  <button className="primary" type="button" onClick={selectedProgram ? registerForHolidayProgram : registerForClass}>
	                    Confirm registration
                  </button>
                </div>
              </>
            )}
          </section>
        )}
      </section>

		      <aside>
		        <VenueList venues={publicData?.venues ?? []} />
		      </aside>
	    </div>
	  );
	}

function CartView({
  schoolSlug,
  registrations,
  classes,
  holidayPrograms,
  autoStartCheckout,
  onAutoStartHandled,
  onReload,
  setError,
  setNotice
}: {
  schoolSlug: string;
  registrations: Registration[];
  classes: ClassItem[];
  holidayPrograms: HolidayProgram[];
  autoStartCheckout: boolean;
  onAutoStartHandled: () => void;
  onReload: () => Promise<void>;
  setError: (message: string) => void;
  setNotice: (message: string) => void;
}) {
  const [checkoutMessage, setCheckoutMessage] = useState("");
  const [isPaying, setIsPaying] = useState(false);

  useEffect(() => {
    if (!autoStartCheckout) return;
    onAutoStartHandled();
    if (registrations[0]) {
      void startCheckout();
    }
  }, [autoStartCheckout, registrations]);

	  const classById = useMemo(() => new Map(classes.map((klass) => [klass.id, klass])), [classes]);
	  const programById = useMemo(() => new Map(holidayPrograms.map((program) => [program.id, program])), [holidayPrograms]);
  const totalCents = registrations.reduce((sum, registration) => sum + Number(registration.priceCents ?? 0), 0);

  async function startCheckout() {
    if (registrations.length === 0) return;
    setCheckoutMessage("");
    setIsPaying(true);
    try {
      const result = await api<CheckoutResult>(`/payments/schools/${schoolSlug}/cart/checkout`, {
        method: "POST",
        body: JSON.stringify({
          paymentBaseUrl: `${window.location.origin}/${schoolSlug}`,
          registrationIds: registrations.map((registration) => registration.id)
        })
      });
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      setCheckoutMessage(result.message ?? "Payment is not available yet. Please contact the school.");
      setNotice(result.message ?? "Payment is not available yet.");
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start checkout");
    } finally {
      setIsPaying(false);
    }
  }

  async function removeRegistration(registration: Registration) {
    try {
      await api(`/portal/schools/${schoolSlug}/registrations/${registration.id}`, { method: "DELETE" });
      setNotice("Item removed from cart.");
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to remove item");
    }
  }

  return (
    <div className="page-grid">
      <section>
        <div className="section-heading">
          <h2>Cart</h2>
          <p>Review unpaid course registrations and continue to payment.</p>
        </div>
        <section className="panel checkout-panel">
          <div className="panel-title">
            <ShoppingCart size={20} />
            <h2>Payment checkout</h2>
          </div>
          {registrations.length === 0 ? (
            <p className="muted">Your cart is empty. Choose a course to add a registration.</p>
          ) : (
            <>
              <div className="cart-total">
                <span>{registrations.length} unpaid item{registrations.length === 1 ? "" : "s"}</span>
                <strong>{formatAud(totalCents)}</strong>
              </div>
              <div className="table-list">
	                {registrations.map((registration) => {
		                  const klass = registration.classId ? classById.get(registration.classId) : undefined;
	                  const program = registration.programId ? programById.get(registration.programId) : undefined;
	                  return (
	                    <article className="row-card cart-item" key={registration.id}>
	                      <div>
	                        <strong>{program?.name ?? klass?.name ?? `Registration ${registration.id}`}</strong>
	                        <span>{registration.student.name}</span>
	                      </div>
	                      <div>
	                        <span>{program ? `${registration.selectedBlocks?.length ?? 0} holiday block(s)` : klass?.schedule ?? "Schedule not loaded"}</span>
	                        <span>{registration.status} · {registration.paymentStatus}</span>
	                        <span>{formatAud(registration.priceCents)}</span>
                      </div>
                      <button className="danger-button" type="button" onClick={() => removeRegistration(registration)}>
                        <Trash2 size={16} />
                        Remove
                      </button>
                    </article>
                  );
                })}
              </div>
            </>
          )}
          {checkoutMessage ? <p className="alert error inline-alert">{checkoutMessage}</p> : null}
          <div className="inline form-actions">
            {registrations[0] ? (
              <button className="primary" type="button" disabled={isPaying} onClick={startCheckout}>
                {isPaying ? "Starting checkout..." : "Pay"}
              </button>
            ) : null}
          </div>
        </section>
      </section>
    </div>
  );
}

function PaymentLinkView({
  schoolSlug,
  token,
  paymentResult,
  setError,
  setNotice
}: {
  schoolSlug: string;
  token: string;
  paymentResult: string;
  setError: (message: string) => void;
  setNotice: (message: string) => void;
}) {
  const [summary, setSummary] = useState<PaymentLinkSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaying, setIsPaying] = useState(false);
  const [checkoutMessage, setCheckoutMessage] = useState("");

  useEffect(() => {
    void loadPaymentLink();
  }, [schoolSlug, token]);

  async function loadPaymentLink() {
    setIsLoading(true);
    setCheckoutMessage("");
    setError("");
    try {
      const result = await api<PaymentLinkSummary>(`/payments/schools/${schoolSlug}/payment-links/${encodeURIComponent(token)}`);
      setSummary(result);
      if (paymentResult === "success") setNotice("Payment completed. Your payment status may take a moment to refresh.");
      if (paymentResult === "cancelled") setCheckoutMessage("Payment was cancelled. You can try again when ready.");
    } catch (err) {
      setSummary(null);
      setError(err instanceof Error ? err.message : "Unable to load payment link");
    } finally {
      setIsLoading(false);
    }
  }

  async function startCheckout() {
    setIsPaying(true);
    setCheckoutMessage("");
    setError("");
    try {
      const paymentBaseUrl = `${window.location.origin}/${schoolSlug}`;
      const result = await api<CheckoutResult>(`/payments/schools/${schoolSlug}/payment-links/${encodeURIComponent(token)}/checkout`, {
        method: "POST",
        body: JSON.stringify({
          paymentBaseUrl,
          successUrl: `${paymentBaseUrl}?pay=${encodeURIComponent(token)}&payment=success`,
          cancelUrl: `${paymentBaseUrl}?pay=${encodeURIComponent(token)}&payment=cancelled`
        })
      });
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      setCheckoutMessage(result.message ?? "Online payment is not available yet. Please contact the school.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start payment");
    } finally {
      setIsPaying(false);
    }
  }

  return (
    <div className="page-grid page-grid-single">
      <section>
        <div className="section-heading split-heading">
          <div>
            <h2>Online payment</h2>
            <p>Review outstanding registrations and pay securely online.</p>
          </div>
          <button type="button" onClick={() => (window.location.href = `/${schoolSlug}`)}>
            <ArrowLeft size={16} />
            Back to courses
          </button>
        </div>
        <section className="panel checkout-panel payment-link-panel">
          <div className="panel-title">
            <CreditCard size={20} />
            <h2>Payment summary</h2>
          </div>
          {isLoading ? <p className="muted">Loading payment details...</p> : null}
          {!isLoading && summary ? (
            <>
              {summary.isPaid ? <p className="alert success inline-alert">These registrations are already paid.</p> : null}
              <div className="cart-total">
                <span>{summary.registrations.length} registration{summary.registrations.length === 1 ? "" : "s"}</span>
                <strong>{formatAud(summary.totalCents)}</strong>
              </div>
              <div className="table-list">
                {summary.registrations.map((registration) => (
                  <article className="row-card payment-link-item" key={registration.id}>
                    <div>
                      <strong>{registration.itemName}</strong>
                      <span>{registration.studentName}</span>
                      {registration.detail ? <span>{registration.detail}</span> : null}
                    </div>
                    <div>
                      <span>{formatAud(registration.amountCents)}</span>
                      <span className={`status-pill ${registration.paymentStatus === "paid" ? "paid" : "unpaid"}`}>{registration.paymentStatus}</span>
                    </div>
                  </article>
                ))}
              </div>
              {!summary.paymentConfigured ? (
                <div className="alert error inline-alert payment-fallback">
                  <strong>Online payment is not configured yet.</strong>
                  {summary.paymentInstructions ? <span>{summary.paymentInstructions}</span> : null}
                  {summary.paymentContactEmail ? <span>Contact {summary.paymentContactEmail} for payment help.</span> : null}
                </div>
              ) : null}
              {checkoutMessage ? <p className="alert error inline-alert">{checkoutMessage}</p> : null}
              {!summary.isPaid ? (
                <div className="inline form-actions">
                  <button className="primary" type="button" disabled={isPaying || !summary.paymentConfigured} onClick={startCheckout}>
                    {isPaying ? "Starting checkout..." : "Pay now"}
                  </button>
                  <button type="button" onClick={loadPaymentLink}>
                    Refresh status
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </section>
      </section>
    </div>
  );
}

function HolidayProgramCard({ program, onRegister }: { program: HolidayProgram; onRegister?: () => void }) {
  const openBlocks = (program.blocks ?? [])
    .filter((block) => block.open !== false && Number(block.bookedCount ?? 0) < Number(block.capacity ?? 0))
    .sort((a, b) => `${a.date} ${a.startTime}`.localeCompare(`${b.date} ${b.startTime}`));
  const openBlockGroups = groupHolidayBlocksByDate(openBlocks);
  return (
    <article className="class-card">
      <div>
        <p className="eyebrow">{program.level || program.ageGroup || "Holiday program"}</p>
        <h3>{program.name}</h3>
        <p>{program.startsAt} - {program.endsAt}</p>
      </div>
      <div className="meta-list">
        <span>
          <MapPin size={16} />
          {program.venueName || program.venueAddress || program.venueId}
        </span>
        <span>
          <Users size={16} />
          {openBlocks.length} available block{openBlocks.length === 1 ? "" : "s"}
        </span>
        {openBlocks.length > 0 ? (
          <div className="holiday-block-summary" aria-label="Available holiday program dates and times">
            <div className="holiday-block-summary-title">
              <CalendarDays size={16} />
              <strong>Available dates and times</strong>
            </div>
            <ul>
              {openBlockGroups.map((group) => (
                <li key={group.date}>
                  <span>{formatHolidayBlockDate(group.blocks[0])}</span>
                  <strong>{group.blocks.map(blockTimeLabel).join(", ")}</strong>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <span>
          <CreditCard size={16} />
          {formatAud(program.blockPriceCents)} incl. GST per block
        </span>
      </div>
      {onRegister ? (
        <button className="primary" onClick={onRegister}>
          Register
        </button>
      ) : null}
    </article>
  );
}

function ClassCard({ klass, t, onRegister }: { klass: ClassItem; t: (typeof copy)["en"]; onRegister?: () => void }) {
  return (
    <article className="class-card">
      <div>
        <p className="eyebrow">{klass.level || klass.ageGroup || "Training class"}</p>
        <h3>{klass.name}</h3>
        <p>{klass.schedule}</p>
      </div>
      <div className="meta-list">
        <span>
          <MapPin size={16} />
          {klass.venueName || klass.venueAddress || klass.venueId}
        </span>
        <span>
          <Users size={16} />
          Capacity {klass.capacity}
        </span>
        <span>
          <CreditCard size={16} />
          {formatAud(klass.priceCents)} {t.gst}
        </span>
      </div>
      {onRegister ? (
        <button className="primary" onClick={onRegister}>
          {t.register}
        </button>
      ) : null}
    </article>
  );
}

function AddStudentView({
	schoolSlug,
	onSaved,
	onCancel,
	setNotice,
	setError
}: {
	schoolSlug: string;
	onSaved: () => Promise<void>;
	onCancel: () => void;
	setNotice: (message: string) => void;
	setError: (message: string) => void;
}) {
	return (
		<section className="single-page">
			<div className="section-heading">
				<h2>Add Student</h2>
				<p>Create a student profile under the current guardian account.</p>
			</div>
			<AddStudentPanel schoolSlug={schoolSlug} onSaved={onSaved} onCancel={onCancel} setNotice={setNotice} setError={setError} />
		</section>
	);
}

function AddStudentEntry({ onClick }: { onClick: () => void }) {
	return (
		<section className="panel compact add-student-entry">
			<div className="panel-title">
				<UserPlus size={20} />
				<h2>Students</h2>
			</div>
			<button className="primary" type="button" onClick={onClick}>
				<UserPlus size={16} />
				Add Student
			</button>
		</section>
	);
}

function AddStudentPanel({
	schoolSlug,
	onSaved,
	onCancel,
	setNotice,
	setError
}: {
	schoolSlug: string;
	onSaved?: () => void | Promise<void>;
	onCancel?: () => void;
	setNotice: (message: string) => void;
	setError: (message: string) => void;
}) {
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      await api(`/portal/schools/${schoolSlug}/students`, {
        method: "POST",
        body: JSON.stringify({
          studentName: String(form.get("studentName")),
          dateOfBirth: String(form.get("dateOfBirth")),
          allergies: String(form.get("allergies")),
          healthNotes: String(form.get("healthNotes")),
          emergencyName: String(form.get("emergencyName")),
          emergencyPhone: String(form.get("emergencyPhone"))
        })
	      });
	      event.currentTarget.reset();
	      setNotice("Student profile added.");
	      await onSaved?.();
	    } catch (err) {
	      setError(err instanceof Error ? err.message : "Unable to add student");
	    }
		  }

			  return (
	    <form className="panel wide-form" onSubmit={submit}>
	      <div className="panel-title">
	        <UserPlus size={20} />
	        <h2>Student details</h2>
	      </div>
      <label>Student name</label>
      <input name="studentName" required />
      <DateOfBirthAgeFields />
      <label>Allergies</label>
      <textarea name="allergies" rows={2} />
      <label>Health conditions</label>
      <textarea name="healthNotes" rows={2} />
      <label>Emergency contact</label>
      <input name="emergencyName" />
      <label>Emergency phone</label>
      <input name="emergencyPhone" type="tel" />
	      <div className="inline form-actions">
	        {onCancel ? (
	          <button type="button" onClick={onCancel}>
	            Back
	          </button>
	        ) : null}
	        <button className="primary" type="submit">
	          Add Student
	        </button>
	      </div>
	    </form>
	  );
	}

function VenueList({ venues }: { venues: Venue[] }) {
  return (
    <section className="panel compact">
      <div className="panel-title">
        <MapPin size={20} />
        <h2>Venues</h2>
      </div>
      {venues.length === 0 ? <p className="muted">No venues configured yet.</p> : null}
      {venues.map((venue) => (
        <div className="venue-row" key={venue.id}>
          <strong>{venue.name}</strong>
          <span>{venue.address}</span>
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address)}`} target="_blank" rel="noreferrer">
            Open directions
          </a>
        </div>
      ))}
    </section>
  );
}

function StudentsView({
  t,
  schoolSlug,
  classes,
  holidayPrograms,
  setError,
  setNotice,
  isAuthed,
  openLogin,
  onCartChanged,
  openCart,
  openAddStudent,
  editStudent
}: {
  t: (typeof copy)["en"];
  schoolSlug: string;
  classes: ClassItem[];
  holidayPrograms: HolidayProgram[];
  setError: (message: string) => void;
  setNotice: (message: string) => void;
  isAuthed: boolean;
  openLogin: () => void;
  onCartChanged: () => Promise<void>;
  openCart: () => void;
  openAddStudent: () => void;
  editStudent: (student: Student) => void;
}) {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [openStudentId, setOpenStudentId] = useState("");

  useEffect(() => {
    if (isAuthed) {
      void load();
    } else {
      setRegistrations([]);
      setStudents([]);
      setOpenStudentId("");
    }
  }, [isAuthed, schoolSlug]);

  async function load() {
    try {
      const [regResult, studentResult] = await Promise.all([
        api<{ registrations: Registration[] }>(`/portal/schools/${schoolSlug}/registrations`),
        api<{ students: Student[] }>(`/portal/schools/${schoolSlug}/students`)
      ]);
      setRegistrations(regResult.registrations);
      setStudents(studentResult.students);
      await onCartChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load students");
    }
  }

  function studentRegistrations(student: Student) {
    return registrations.filter((reg) => reg.studentId === student.id || reg.student?.name === student.name);
  }

  function classFor(registration: Registration) {
    return classes.find((klass) => klass.id === registration.classId);
  }

  function programFor(registration: Registration) {
    return holidayPrograms.find((program) => program.id === registration.programId);
  }

  return (
    <div className="page-grid">
      <section>
        <div className="section-heading split-heading">
          <div>
            <h2>Students</h2>
            <p>Manage student profiles and review each student's course enrollments.</p>
          </div>
          {isAuthed ? (
            <button className="primary" type="button" onClick={openAddStudent}>
              <UserPlus size={16} />
              Add Student
            </button>
          ) : null}
        </div>
        {!isAuthed ? (
          <section className="panel compact">
            <div className="panel-title">
              <Smartphone size={20} />
              <h2>Sign in to view students</h2>
            </div>
            <p className="muted">Sign in to review student profiles, enrollments, payments and attendance.</p>
            <button className="primary" type="button" onClick={openLogin}>
              Sign in
            </button>
          </section>
        ) : null}
        <div className="table-list">
          {isAuthed && students.length === 0 ? <p className="muted">No student profiles yet. Use Add Student to create one.</p> : null}
          {students.map((student) => {
            const enrollments = studentRegistrations(student);
            const isOpen = openStudentId === student.id;
            return (
              <article className="row-card student-card" key={student.id}>
                <div>
                  <strong>{student.name}</strong>
                  <span>DOB {student.dateOfBirth || "Not recorded"}</span>
                </div>
                <div className="student-details">
                  <span>Age: {studentAgeLabel(student.dateOfBirth)}</span>
                  <span>{t.health}: {student.healthNotes || "None recorded"}</span>
                  <span>Allergies: {student.allergies || "None recorded"}</span>
                  <span>Emergency: {student.emergencyContact?.name || "Not recorded"} {student.emergencyContact?.phone || ""}</span>
                </div>
                <div className="student-actions">
                  <button type="button" onClick={() => setOpenStudentId(isOpen ? "" : student.id)}>
                    Enrollments
                  </button>
                  <button className="primary" type="button" onClick={() => editStudent(student)}>
                    Edit Student
                  </button>
                </div>
                {isOpen ? (
                  <div className="enrollment-list">
                    {enrollments.length === 0 ? <p className="muted">No enrollments recorded for this student yet.</p> : null}
                    {enrollments.map((registration) => {
                      const klass = classFor(registration);
                      const program = programFor(registration);
                      return (
                        <div className="enrollment-item" key={registration.id}>
                          <div>
                            <strong>{program?.name ?? klass?.name ?? "Course"}</strong>
                            <span>{program ? `${registration.selectedBlocks?.length ?? 0} holiday block(s)` : klass?.schedule ?? "Schedule not recorded"}</span>
                          </div>
                          <div>
                            <span>{registration.status}</span>
                            <span>{t.payment}: {registration.paymentStatus}</span>
                          </div>
                          {registration.status !== "cancelled" && registration.paymentStatus !== "paid" ? (
                            <button type="button" onClick={openCart}>
                              Pay from cart
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </section>
      <aside>
        <section className="panel compact">
          <div className="panel-title">
            <Users size={20} />
            <h2>Student profiles</h2>
          </div>
          <p className="muted">Each student keeps health notes, allergies and emergency contacts for coach access.</p>
        </section>
      </aside>
    </div>
  );
}

function MyAccountView({
  schoolSlug,
  isAuthed,
  sessionProfile,
  setError,
  setNotice,
  onSaved,
  openLogin
}: {
  schoolSlug: string;
  isAuthed: boolean;
  sessionProfile: SessionProfile | null;
  setError: (message: string) => void;
  setNotice: (message: string) => void;
  onSaved: () => Promise<void>;
  openLogin: () => void;
}) {
  const [guardian, setGuardian] = useState<GuardianProfile | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isAuthed && sessionProfile?.role === "guardian") {
      void load();
    }
  }, [isAuthed, schoolSlug, sessionProfile?.role]);

  async function load() {
    try {
      const result = await api<{ guardian?: GuardianProfile }>(`/portal/schools/${schoolSlug}/students`);
      setGuardian(result.guardian ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load account details");
    }
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const isStaffProfile = sessionProfile?.role !== "guardian";
      const result = await api<{ guardian?: GuardianProfile; staff?: StaffProfile }>(`/portal/schools/${schoolSlug}/profile`, {
        method: "PUT",
        body: JSON.stringify(isStaffProfile ? { name: String(form.get("name")) } : { parentName: String(form.get("parentName")) })
      });
      if (result.guardian) setGuardian(result.guardian);
      setNotice("Profile updated.");
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update account details");
    } finally {
      setIsSaving(false);
    }
  }

  if (!isAuthed) {
    return (
      <section className="panel compact">
        <div className="panel-title">
          <Smartphone size={20} />
          <h2>Sign in to view your account</h2>
        </div>
        <button className="primary" type="button" onClick={openLogin}>
          Sign in
        </button>
      </section>
    );
  }

  if (sessionProfile?.role !== "guardian") {
    return (
      <div className="page-grid">
        <section>
          <div className="section-heading">
            <h2>My profile</h2>
            <p>Update your display name and review your sign-in details.</p>
          </div>
          <form className="panel wide-form" onSubmit={save} key={sessionProfile?.staff?.name ?? sessionProfile?.userKey ?? "staff"}>
            <div className="panel-title">
              <ShieldCheck size={20} />
              <h2>Staff details</h2>
            </div>
            <label>Name</label>
            <input name="name" defaultValue={sessionProfile?.staff?.name ?? ""} />
            <div className="detail-grid profile-detail-grid">
              <span>
                <strong>Role</strong>
                {sessionProfile?.role ?? "staff"}
              </span>
              <span>
                <Phone size={16} />
                {sessionProfile?.phone || "No phone recorded"}
              </span>
              <span>
                <Mail size={16} />
                {sessionProfile?.email || sessionProfile?.staff?.email || "No email recorded"}
              </span>
            </div>
            <p className="muted">Email and phone are sign-in details. Admin can update them from Staff settings.</p>
            <button className="primary" type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save profile"}
            </button>
          </form>
        </section>
      </div>
    );
  }

  return (
    <div className="page-grid">
      <section>
        <div className="section-heading">
          <h2>My Account</h2>
          <p>Review and update guardian details and login information.</p>
        </div>
        <form className="panel wide-form" onSubmit={save}>
          <div className="panel-title">
            <ShieldCheck size={20} />
            <h2>Guardian details</h2>
          </div>
          <label>Parent / guardian name</label>
          <input name="parentName" defaultValue={guardian?.name ?? ""} />
          <label>Login phone</label>
          <input className="readonly-input" value={guardian?.phone ?? sessionProfile?.phone ?? ""} readOnly />
          <label>Login email</label>
          <input className="readonly-input" value={guardian?.email ?? sessionProfile?.email ?? ""} readOnly />
          <p className="muted">Email and phone are sign-in details. Ask staff to update them if they need to change.</p>
          <button className="primary" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save profile"}
          </button>
        </form>
      </section>
    </div>
  );
}

function EditStudentView({
  schoolSlug,
  student,
  setNotice,
  setError,
  onSaved,
  onCancel
}: {
  schoolSlug: string;
  student: Student;
  setNotice: (message: string) => void;
  setError: (message: string) => void;
  onSaved: () => Promise<void>;
  onCancel: () => void;
}) {
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api(`/portal/schools/${schoolSlug}/students/${student.id}`, {
        method: "PUT",
        body: JSON.stringify({
          studentName: String(form.get("studentName")),
          dateOfBirth: String(form.get("dateOfBirth")),
          allergies: String(form.get("allergies")),
          healthNotes: String(form.get("healthNotes")),
          emergencyName: String(form.get("emergencyName")),
          emergencyPhone: String(form.get("emergencyPhone"))
        })
      });
      setNotice("Student profile updated.");
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update student");
    }
  }

  return (
    <div className="page-grid">
      <section>
        <div className="section-heading">
          <h2>Edit Student</h2>
          <p>Update health details, allergies and emergency contact information.</p>
        </div>
        <form className="panel wide-form" onSubmit={submit}>
          <div className="panel-title">
            <UserPlus size={20} />
            <h2>{student.name}</h2>
          </div>
          <label>Student name</label>
          <input name="studentName" defaultValue={student.name} required />
          <DateOfBirthAgeFields defaultValue={student.dateOfBirth} />
          <label>Allergies</label>
          <textarea name="allergies" rows={2} defaultValue={student.allergies ?? ""} />
          <label>Health conditions</label>
          <textarea name="healthNotes" rows={2} defaultValue={student.healthNotes ?? ""} />
          <div className="two-cols">
            <div>
              <label>Emergency contact</label>
              <input name="emergencyName" defaultValue={student.emergencyContact?.name ?? ""} required />
            </div>
            <div>
              <label>Emergency phone</label>
              <input name="emergencyPhone" type="tel" defaultValue={student.emergencyContact?.phone ?? ""} required />
            </div>
          </div>
          <div className="inline form-actions">
            <button type="button" onClick={onCancel}>
              Back
            </button>
            <button className="primary" type="submit">
              Save Student
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function AdminStudentProfilePage({
  schoolSlug,
  studentId,
  classes,
  holidayPrograms,
  setNotice,
  setError,
  onSaved,
  onBack
}: {
  schoolSlug: string;
  studentId: string;
  classes: ClassItem[];
  holidayPrograms: HolidayProgram[];
  setNotice: (message: string) => void;
  setError: (message: string) => void;
  onSaved: () => Promise<void>;
  onBack: () => void;
}) {
  const [student, setStudent] = useState<Student | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const classById = useMemo(() => new Map(classes.map((klass) => [klass.id, klass])), [classes]);
  const programById = useMemo(() => new Map(holidayPrograms.map((program) => [program.id, program])), [holidayPrograms]);

  useEffect(() => {
    void loadStudentProfile();
  }, [schoolSlug, studentId]);

  async function loadStudentProfile() {
    setIsLoading(true);
    setError("");
    try {
      const result = await api<{ student: Student; registrations: Registration[] }>(`/admin/schools/${schoolSlug}/students/${encodeURIComponent(studentId)}`);
      setStudent(result.student);
      setRegistrations(result.registrations ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load student profile");
    } finally {
      setIsLoading(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const result = await api<{ student: Student; registrations: Registration[] }>(`/admin/schools/${schoolSlug}/students/${encodeURIComponent(studentId)}`, {
        method: "PUT",
        body: JSON.stringify({
          studentName: String(form.get("studentName")),
          dateOfBirth: String(form.get("dateOfBirth")),
          parentName: String(form.get("parentName")),
          guardianEmail: String(form.get("guardianEmail")),
          allergies: String(form.get("allergies")),
          healthNotes: String(form.get("healthNotes")),
          emergencyName: String(form.get("emergencyName")),
          emergencyPhone: String(form.get("emergencyPhone"))
        })
      });
      setStudent(result.student);
      setRegistrations(result.registrations ?? []);
      setNotice("Student profile updated.");
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update student profile");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="admin-student-profile-page">
      <div className="section-heading split-heading">
        <div>
          <h2>Student profile</h2>
          <p>Review and update the student's contact, emergency and health details.</p>
        </div>
        <button type="button" onClick={onBack}>
          <ArrowLeft size={16} />
          Back to registrations
        </button>
      </div>
      {isLoading ? (
        <section className="panel compact">
          <p className="muted">Loading student profile...</p>
        </section>
      ) : student ? (
        <>
          <form className="panel wide-form admin-student-profile" key={`${student.id}-${student.name}-${student.dateOfBirth}`} onSubmit={submit}>
            <div className="panel-title">
              <UserCircle size={20} />
              <h2>{student.name || "Unnamed student"}</h2>
            </div>
            <label>Student name</label>
            <input name="studentName" defaultValue={student.name} required />
            <DateOfBirthAgeFields defaultValue={student.dateOfBirth} />
            <label>Parent / guardian name</label>
            <input name="parentName" defaultValue={student.parent?.name ?? ""} />
            <label>Guardian email</label>
            <input name="guardianEmail" type="email" defaultValue={student.parent?.email ?? ""} />
            <div className="two-cols">
              <div>
                <label>Emergency contact</label>
                <input name="emergencyName" defaultValue={student.emergencyContact?.name ?? ""} />
              </div>
              <div>
                <label>Emergency phone</label>
                <input name="emergencyPhone" type="tel" defaultValue={student.emergencyContact?.phone ?? ""} />
              </div>
            </div>
            <label>Allergies</label>
            <textarea name="allergies" rows={2} defaultValue={student.allergies ?? ""} />
            <label>Health conditions</label>
            <textarea name="healthNotes" rows={3} defaultValue={student.healthNotes ?? ""} />
            <div className="inline form-actions">
              <button type="button" onClick={onBack}>
                Cancel
              </button>
              <button className="primary" type="submit" disabled={isSaving}>
                {isSaving ? "Saving..." : "Save student profile"}
              </button>
            </div>
          </form>
          <section className="panel admin-student-registrations">
            <div className="admin-list-header">
              <div>
                <h2>Registrations</h2>
                <p className="muted">{registrations.length} registration{registrations.length === 1 ? "" : "s"} linked to this student</p>
              </div>
            </div>
            <div className="admin-student-registration-list">
              {registrations.length === 0 ? <p className="muted">No registrations recorded for this student.</p> : null}
              {registrations.map((registration) => {
                const isHoliday = registration.programType === "holiday";
                const klass = registration.classId ? classById.get(registration.classId) : undefined;
                const program = registration.programId ? programById.get(registration.programId) : undefined;
                const title = isHoliday ? program?.name ?? "Holiday program" : klass?.name ?? "Term class";
                const schedule = isHoliday ? holidayRegistrationSummary(registration) : klass?.schedule || "Schedule not recorded";
                return (
                  <article className="admin-student-registration-row" key={registration.id}>
                    <div>
                      <strong>{title}</strong>
                      <span>{schedule}</span>
                    </div>
                    <span className="status-pill">{isHoliday ? "Holiday" : "Class"}</span>
                    <strong>{formatAud(Number(registration.priceCents ?? 0))}</strong>
                    <span className={`status-pill ${registration.paymentStatus === "paid" ? "paid" : "unpaid"}`}>{registration.paymentStatus}</span>
                  </article>
                );
              })}
            </div>
          </section>
        </>
      ) : (
        <section className="panel compact">
          <p className="muted">Student profile was not found.</p>
        </section>
      )}
    </div>
  );
}

function AdminRegistrationEditPage({
  schoolSlug,
  registration,
  classes,
  holidayPrograms,
  setNotice,
  setError,
  onSaved,
  onBack
}: {
  schoolSlug: string;
  registration: Registration;
  classes: ClassItem[];
  holidayPrograms: HolidayProgram[];
  setNotice: (message: string) => void;
  setError: (message: string) => void;
  onSaved: () => Promise<void>;
  onBack: () => void;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isHoliday = registration.programType === "holiday";
  const program = isHoliday ? holidayPrograms.find((item) => item.id === registration.programId) : undefined;
  const currentClass = registration.classId ? classes.find((klass) => klass.id === registration.classId) : undefined;
  const itemName = program?.name ?? currentClass?.name ?? "Registration";
  const canDelete = registration.paymentStatus === "unpaid";

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api(`/admin/schools/${schoolSlug}/registrations/${encodeURIComponent(registration.id)}`, {
        method: "PUT",
        body: JSON.stringify({
          classId: isHoliday ? undefined : String(form.get("classId")),
          status: String(form.get("status")),
          priceCents: Math.round(Number(form.get("priceAud") || 0) * 100),
          paymentStatus: String(form.get("paymentStatus")),
          paymentMethod: String(form.get("paymentMethod")),
          paymentNote: String(form.get("paymentNote")),
          amountPaidCents: Math.round(Number(form.get("amountPaidAud") || 0) * 100)
        })
      });
      setNotice("Registration updated.");
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update registration");
    } finally {
      setIsSaving(false);
    }
  }

  async function cancelRegistration() {
    if (!window.confirm(`Cancel registration for ${registration.student?.name || "this student"}? The history will remain, but it will not appear in coach roster or unpaid reminders.`)) return;
    setIsSaving(true);
    setError("");
    try {
      await api(`/admin/schools/${schoolSlug}/registrations/${encodeURIComponent(registration.id)}`, {
        method: "PUT",
        body: JSON.stringify({
          status: "cancelled",
          priceCents: registration.priceCents,
          paymentNote: registration.paymentNote || "Cancelled by admin"
        })
      });
      setNotice("Registration cancelled.");
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to cancel registration");
    } finally {
      setIsSaving(false);
    }
  }

  async function deleteRegistration() {
    if (!window.confirm(`Delete this unpaid registration for ${registration.student?.name || "this student"}? This is only for records created by mistake.`)) return;
    setIsDeleting(true);
    setError("");
    try {
      await api(`/admin/schools/${schoolSlug}/registrations/${encodeURIComponent(registration.id)}`, { method: "DELETE" });
      setNotice("Registration deleted.");
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to delete registration");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <section className="panel admin-form-page registration-edit-page">
      <div className="admin-form-header">
        <button type="button" onClick={onBack}>
          <ArrowLeft size={16} />
          Back to registrations
        </button>
        <div>
          <p className="eyebrow">Registration</p>
          <h2>Edit registration</h2>
          <p className="muted">{registration.student?.name || "Student"} · {itemName}</p>
        </div>
      </div>
      <form className="admin-registration-edit-form" onSubmit={save}>
        <div className="registration-detail-grid">
          <div>
            <span>Student</span>
            <strong>{registration.student?.name || "Unnamed student"}</strong>
            <small>{studentAgeSentence(registration.student?.dateOfBirth)}</small>
          </div>
          <div>
            <span>Parent</span>
            <strong>{registration.parent?.name || "Not recorded"}</strong>
            <small>{registration.guardianEmail || "No email"}</small>
          </div>
          <div>
            <span>Created</span>
            <strong>{registration.createdAt?.slice(0, 10) || "Not recorded"}</strong>
            <small>{registration.id}</small>
          </div>
          <div>
            <span>Type</span>
            <strong>{isHoliday ? "Holiday program" : "Term class"}</strong>
            <small>{registration.reEnrollmentCampaignId ? "Re-enrolment" : "Direct registration"}</small>
          </div>
        </div>
        {isHoliday ? (
          <div>
            <label>Program</label>
            <input className="readonly-input" value={program?.name || registration.programId || "Holiday program"} readOnly />
          </div>
        ) : (
          <div>
            <label>Class</label>
            <select name="classId" defaultValue={registration.classId || ""} required>
              <option value="" disabled>Select class</option>
              {classes.map((klass) => (
                <option value={klass.id} key={klass.id}>
                  {klass.termName ? `${klass.termName} · ` : ""}{klass.name}{klass.schedule ? ` · ${klass.schedule}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="two-cols">
          <div>
            <label>Registration status</label>
            <select name="status" defaultValue={registration.status || "confirmed"}>
              <option value="confirmed">confirmed</option>
              <option value="waitlist">waitlist</option>
              <option value="cancelled">cancelled</option>
            </select>
          </div>
          <div>
            <label>Price AUD</label>
            <input name="priceAud" type="number" min="0" step="0.01" defaultValue={centsToAudInput(registration.priceCents)} required />
          </div>
        </div>
        <div className="two-cols">
          <div>
            <label>Payment status</label>
            <select name="paymentStatus" defaultValue={registration.paymentStatus || "unpaid"}>
              <option value="unpaid">unpaid</option>
              <option value="paid">paid</option>
              <option value="refunded">refunded</option>
              <option value="cancelled">cancelled</option>
            </select>
          </div>
          <div>
            <label>Amount paid AUD</label>
            <input name="amountPaidAud" type="number" min="0" step="0.01" defaultValue={centsToAudInput(registration.amountPaidCents)} />
          </div>
        </div>
        <label>Payment method</label>
        <input name="paymentMethod" defaultValue={registration.paymentMethod || ""} placeholder="Manual, Stripe Checkout, bank transfer" />
        <label>Payment note</label>
        <textarea name="paymentNote" rows={3} defaultValue={registration.paymentNote || ""} />
        <div className="inline form-actions">
          <button type="button" onClick={onBack}>Cancel</button>
          <button className="primary" type="submit" disabled={isSaving || isDeleting}>
            {isSaving ? "Saving..." : "Save registration"}
          </button>
          <button type="button" onClick={cancelRegistration} disabled={isSaving || isDeleting || registration.status === "cancelled"}>
            Cancel registration
          </button>
          <button className="danger-button" type="button" onClick={deleteRegistration} disabled={isSaving || isDeleting || !canDelete} title={canDelete ? "Delete unpaid registration" : "Only unpaid registrations can be deleted"}>
            <Trash2 size={16} />
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </form>
    </section>
  );
}

function AdminView({
  t,
  schoolSlug,
  classes,
  terms,
  holidayPrograms,
  refresh,
  setError,
  setNotice,
  isAuthed,
  isStaff,
  staffRole,
  sessionProfile,
  initialCollection,
  openLogin
}: {
  t: (typeof copy)["en"];
  schoolSlug: string;
  classes: ClassItem[];
  terms: Term[];
  holidayPrograms: HolidayProgram[];
  refresh: () => Promise<void>;
  setError: (message: string) => void;
  setNotice: (message: string) => void;
  isAuthed: boolean;
  setIsAuthed: (value: boolean) => void;
  isStaff: boolean;
  staffRole?: SessionProfile["role"];
  sessionProfile: SessionProfile | null;
  initialCollection?: string;
  openLogin: () => void;
}) {
  const [collection, setCollection] = useState(initialCollection ?? "registrations");
  const [items, setItems] = useState<Record<string, unknown>[]>([]);
  const [lookupItems, setLookupItems] = useState<{ venues: Record<string, unknown>[]; terms: Record<string, unknown>[]; classTemplates: ClassTemplate[]; members: Record<string, unknown>[]; coverages: Record<string, unknown>[] }>({ venues: [], terms: [], classTemplates: [], members: [], coverages: [] });
  const [selectedItem, setSelectedItem] = useState<Record<string, unknown> | null>(null);
  const [selectedAdminStudentId, setSelectedAdminStudentId] = useState("");
  const [selectedAdminRegistration, setSelectedAdminRegistration] = useState<Registration | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const availableCollections = adminCollectionsForRole(staffRole);
  const isAdminStaff = staffRole === "admin";

  useEffect(() => {
    if (isAuthed && isStaff) {
      const nextCollection = availableCollections.includes(collection) ? collection : availableCollections[0] ?? "registrations";
      if (nextCollection !== collection) {
        setCollection(nextCollection);
        return;
      }
      void loadItems(nextCollection);
      if (isAdminStaff) void loadLookupItems();
    }
  }, [isAuthed, isStaff, schoolSlug, collection, staffRole]);

  async function loadItems(nextCollection = collection) {
    try {
      if (nextCollection === "registrations") {
        const result = await api<{ registrations: Record<string, unknown>[] }>(`/admin/schools/${schoolSlug}/registrations`);
        setItems(result.registrations);
        setSelectedItem(null);
        return;
      }
      if (nextCollection === "payments-emails") {
        setItems([]);
        setSelectedItem(null);
        return;
      }
      if (nextCollection === "reports-attendance" || nextCollection === "re-enrolments") {
        setItems([]);
        setSelectedItem(null);
        return;
      }
      if (!isAdminStaff) {
        setItems([]);
        setSelectedItem(null);
        return;
      }
      const result = await api<{ items: Record<string, unknown>[] }>(`/admin/schools/${schoolSlug}/${nextCollection}`);
      setItems(result.items);
      setSelectedItem((current) => {
        if (!current) return null;
        return result.items.find((item) => adminItemKey(item) === adminItemKey(current)) ?? null;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load admin data");
    }
  }

  async function loadLookupItems() {
    if (!isAdminStaff) return;
    try {
      const [venuesResult, termsResult, templatesResult, membersResult, coveragesResult] = await Promise.all([
        api<{ items: Record<string, unknown>[] }>(`/admin/schools/${schoolSlug}/venues`),
        api<{ items: Record<string, unknown>[] }>(`/admin/schools/${schoolSlug}/terms`),
        api<{ items: ClassTemplate[] }>(`/admin/schools/${schoolSlug}/class-templates`),
        api<{ items: Record<string, unknown>[] }>(`/admin/schools/${schoolSlug}/members`),
        api<{ items: Record<string, unknown>[] }>(`/admin/schools/${schoolSlug}/coach-coverages`)
      ]);
      setLookupItems({ venues: venuesResult.items, terms: termsResult.items, classTemplates: templatesResult.items, members: membersResult.items, coverages: coveragesResult.items });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load admin dropdown options");
    }
  }

  function switchCollection(name: string) {
    setCollection(name);
    setSelectedItem(null);
    setSelectedAdminStudentId("");
    setSelectedAdminRegistration(null);
    setFormMode("create");
    setIsFormOpen(false);
  }

  function startCreate() {
    setSelectedItem(null);
    setSelectedAdminStudentId("");
    setSelectedAdminRegistration(null);
    setFormMode("create");
    setIsFormOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function startEdit(item: Record<string, unknown>) {
    setSelectedItem(item);
    setSelectedAdminStudentId("");
    setSelectedAdminRegistration(null);
    setFormMode("edit");
    setIsFormOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function startDuplicate(item: Record<string, unknown>) {
    setSelectedItem(item);
    setSelectedAdminStudentId("");
    setSelectedAdminRegistration(null);
    setFormMode("create");
    setIsFormOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function openAdminStudentProfile(studentId?: string) {
    if (!studentId) return;
    setSelectedAdminStudentId(studentId);
    setSelectedAdminRegistration(null);
    setSelectedItem(null);
    setIsFormOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function openAdminRegistrationEdit(registration: Registration) {
    setSelectedAdminRegistration(registration);
    setSelectedAdminStudentId("");
    setSelectedItem(null);
    setIsFormOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function closeForm() {
    setIsFormOpen(false);
    setSelectedItem(null);
    setSelectedAdminRegistration(null);
    setFormMode("create");
  }

  async function saveItem(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = Object.fromEntries(new FormData(event.currentTarget).entries());
    const itemId = selectedItem ? adminItemKey(selectedItem) : "";
    try {
      const payload = normalizeAdminPayload(collection, data);
      if (formMode === "edit" && itemId) {
        await api(`/admin/schools/${schoolSlug}/${collection}/${encodeURIComponent(itemId)}`, {
          method: "PUT",
          body: JSON.stringify(payload)
        });
        setNotice(`${collection} item updated.`);
      } else {
        await api(`/admin/schools/${schoolSlug}/${collection}`, {
          method: "POST",
          body: JSON.stringify(payload)
        });
        setNotice(`${collection} item created.`);
      }
      await loadItems();
      if (isAdminStaff) await loadLookupItems();
      await refresh();
      setIsFormOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save item");
    }
  }

  async function deleteItem(item: Record<string, unknown>) {
    const itemId = adminItemKey(item);
    const title = adminItemTitle(collection, item);
    if (!window.confirm(`Delete ${title}? This staff member will lose access immediately.`)) return;
    setError("");
    try {
      await api(`/admin/schools/${schoolSlug}/${collection}/${encodeURIComponent(itemId)}`, { method: "DELETE" });
      setNotice(`${title} deleted.`);
      await loadItems();
      if (isAdminStaff) await loadLookupItems();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to delete staff member");
    }
  }

  const collectionLabel = adminCollectionLabel(collection);
  const canUseAdmin = isAuthed && isAdminStaff;
  const showAdminStudentPage = canUseAdmin && Boolean(selectedAdminStudentId);
  const showAdminRegistrationEditPage = canUseAdmin && Boolean(selectedAdminRegistration);
  const showNotificationComposer = canUseAdmin && collection === "notifications" && isFormOpen;
  const showNotificationView = canUseAdmin && collection === "notifications" && Boolean(selectedItem) && !isFormOpen;
  const showAdminFormPage = canUseAdmin && collection !== "registrations" && collection !== "notifications" && isFormOpen;
  const headingTitle = staffRole === "coach" ? "Coach roster" : staffRole === "finance" ? "Registrations & Payments" : "Business settings";
  const headingCopy =
    staffRole === "coach"
      ? "Review students assigned to your classes and holiday program blocks."
      : staffRole === "finance"
        ? "Review registrations, payment status and unpaid reminders."
        : "Configure venues, terms, fixed classes, staff roles and class announcements.";
  const currentStaffKey = sessionStaffKey(sessionProfile);

  return (
    <div className="page-grid page-grid-single">
      <section>
        <div className="section-heading">
          <h2>{headingTitle}</h2>
          <p>{headingCopy}</p>
        </div>
        {!isAuthed ? (
          <section className="panel compact">
            <div className="panel-title">
              <Smartphone size={20} />
              <h2>Admin sign in required</h2>
            </div>
            <p className="muted">Admin opens the business configuration area after an authorised staff member signs in.</p>
            <button className="primary" type="button" onClick={openLogin}>
              Sign in
            </button>
          </section>
        ) : !isStaff ? (
          <section className="panel compact">
            <div className="panel-title">
              <ShieldCheck size={20} />
              <h2>Staff access required</h2>
            </div>
            <p className="muted">Business settings are only available to staff roles.</p>
          </section>
        ) : (
          <>
            {showAdminRegistrationEditPage && selectedAdminRegistration ? (
              <AdminRegistrationEditPage
                schoolSlug={schoolSlug}
                registration={selectedAdminRegistration}
                classes={classes}
                holidayPrograms={holidayPrograms}
                setError={setError}
                setNotice={setNotice}
                onBack={() => setSelectedAdminRegistration(null)}
                onSaved={async () => {
                  await loadItems("registrations");
                  await refresh();
                  setSelectedAdminRegistration(null);
                }}
              />
            ) : showAdminStudentPage ? (
              <AdminStudentProfilePage
                schoolSlug={schoolSlug}
                studentId={selectedAdminStudentId}
                classes={classes}
                holidayPrograms={holidayPrograms}
                setError={setError}
                setNotice={setNotice}
                onBack={() => setSelectedAdminStudentId("")}
                onSaved={async () => {
                  await loadItems("registrations");
                  await refresh();
                }}
              />
            ) : showNotificationComposer ? (
              <AdminNotificationComposer
                schoolSlug={schoolSlug}
                terms={terms}
                classes={classes}
                initialItem={formMode === "create" ? selectedItem : null}
                setError={setError}
                setNotice={setNotice}
                onCancel={closeForm}
                onSent={async () => {
                  await loadItems("notifications");
                  await refresh();
                  closeForm();
                }}
              />
            ) : showNotificationView && selectedItem ? (
              <AdminNotificationSentView
                item={selectedItem}
                onBack={() => setSelectedItem(null)}
                onDuplicate={() => {
                  setFormMode("create");
                  setIsFormOpen(true);
                }}
              />
            ) : showAdminFormPage ? (
              <form className="panel admin-form-page" key={`${collection}-${formMode}-${selectedItem ? adminItemKey(selectedItem) : "new"}`} onSubmit={saveItem}>
                <div className="admin-form-header">
                  <button type="button" onClick={closeForm}>
                    <ArrowLeft size={16} />
                    Back to {collectionLabel}
                  </button>
                  <div>
                    <p className="eyebrow">{collectionLabel}</p>
                    <h2>{formMode === "edit" ? "Edit" : selectedItem ? "Duplicate" : t.create} {collectionLabel}</h2>
                    <p className="muted">{formMode === "edit" ? "Update this record, then save changes." : selectedItem ? "Review the copied fields, then create a new record." : "Create this record on a separate page."}</p>
                  </div>
                </div>
                <AdminFields
                  collection={collection}
                  item={formMode === "edit" || selectedItem ? selectedItem : null}
                  venues={lookupItems.venues}
                  terms={lookupItems.terms}
                  classTemplates={lookupItems.classTemplates}
                  members={lookupItems.members}
                  classes={classes}
                  holidayPrograms={holidayPrograms}
                />
                <div className="inline form-actions">
                  <button type="button" onClick={closeForm}>
                    Cancel
                  </button>
                  <button className="primary" type="submit">
                    {formMode === "edit" ? "Save changes" : t.create}
                  </button>
                </div>
              </form>
            ) : (
              <>
            {availableCollections.length > 1 ? (
              <AdminCollectionNav collection={collection} onSwitch={switchCollection} staffRole={staffRole} />
            ) : null}
            {collection === "payments-emails" ? (
              <AdminPaymentEmailSettingsPanel
                schoolSlug={schoolSlug}
                setError={setError}
                setNotice={setNotice}
                refresh={refresh}
              />
            ) : collection === "reports-attendance" ? (
              <AdminAttendanceReportPanel
                schoolSlug={schoolSlug}
                terms={terms}
                classes={classes}
                members={lookupItems.members as unknown as StaffMember[]}
                setError={setError}
                onOpenStudentProfile={openAdminStudentProfile}
              />
            ) : collection === "re-enrolments" ? (
              <AdminReEnrollmentPanel
                schoolSlug={schoolSlug}
                terms={terms}
                classes={classes}
                setError={setError}
                setNotice={setNotice}
                onCreated={async () => {
                  await loadItems("registrations");
                  await refresh();
                }}
              />
            ) : collection === "registrations" ? (
              staffRole === "coach" ? (
                <CoachSchedulePanel
                  schoolSlug={schoolSlug}
                  registrations={items as unknown as Registration[]}
                  classes={classes}
                  terms={terms}
                  holidayPrograms={holidayPrograms}
                  setError={setError}
                  setNotice={setNotice}
                />
              ) : (
                <AdminRegistrationsPanel
                  registrations={items as unknown as Registration[]}
                  classes={classes}
                  holidayPrograms={holidayPrograms}
                  schoolSlug={schoolSlug}
                  staffRole={staffRole}
                  setError={setError}
                  setNotice={setNotice}
                  onReload={() => loadItems("registrations")}
                  canOpenStudentProfile={staffRole === "admin"}
                  onOpenStudentProfile={openAdminStudentProfile}
                  onOpenRegistrationEdit={openAdminRegistrationEdit}
                />
              )
            ) : collection === "notifications" ? (
              <AdminNotificationsPanel
                items={items}
                onCreate={startCreate}
                onView={(item) => {
                  setSelectedItem(item);
                  setIsFormOpen(false);
                }}
                onDuplicate={(item) => {
                  setSelectedItem(item);
                  setFormMode("create");
                  setIsFormOpen(true);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            ) : collection === "classes" ? (
              <AdminClassesPanel
                items={items}
                terms={terms}
                members={lookupItems.members as unknown as StaffMember[]}
                onCreate={startCreate}
                onEdit={startEdit}
                onDuplicate={startDuplicate}
              />
            ) : (
              <section className="panel admin-list-panel">
                <div className="admin-list-header">
                  <div>
                    <h2>Existing {collectionLabel}</h2>
                    <p className="muted">{items.length} item{items.length === 1 ? "" : "s"}</p>
                  </div>
                  <button className="primary" type="button" onClick={startCreate}>
                    <LayoutDashboard size={16} />
                    Create New {collectionLabel}
                  </button>
                </div>
                <div className="table-list">
                  {items.length === 0 ? <p className="muted">No {collectionLabel.toLowerCase()} configured yet.</p> : null}
                  {adminListItems(collection, items).map((item) => {
                    const deleteReason =
                      collection === "members"
                        ? staffDeleteBlockReason(item, currentStaffKey, items, classes, holidayPrograms, lookupItems.coverages)
                        : "";
                    return (
                      <article className={`row-card admin-item ${selectedItem && adminItemKey(selectedItem) === adminItemKey(item) ? "selected" : ""}`} key={adminItemKey(item)}>
                        <div>
                          <strong>{adminItemTitle(collection, item)}</strong>
                          <span>{adminItemSubtitle(collection, item, lookupItems.classTemplates)}</span>
                          {deleteReason ? <small className="muted">{deleteReason}</small> : null}
                        </div>
                        <div className="admin-row-actions">
                          <button type="button" onClick={() => startEdit(item)}>
                            Edit
                          </button>
                          {collection === "members" ? (
                            <button className="danger-button" type="button" disabled={Boolean(deleteReason)} title={deleteReason || `Delete ${adminItemTitle(collection, item)}`} onClick={() => deleteItem(item)}>
                              <Trash2 size={16} />
                              Delete
                            </button>
                          ) : null}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            )}
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function AdminCollectionNav({
  collection,
  onSwitch,
  staffRole
}: {
  collection: string;
  onSwitch: (collection: string) => void;
  staffRole?: SessionProfile["role"];
}) {
  if (staffRole !== "admin") {
    return (
      <div className="segmented">
        {adminCollectionsForRole(staffRole).map((name) => (
          <button key={name} className={collection === name ? "active" : ""} onClick={() => onSwitch(name)}>
            {adminCollectionLabel(name)}
          </button>
        ))}
      </div>
    );
  }
  const primaryCollections = ["registrations", "re-enrolments", "reports-attendance", "payments-emails"];
  const setupCollections = ["venues", "terms", "class-templates", "classes", "holiday-programs", "coach-coverages", "members", "notifications"];
  const setupValue = setupCollections.includes(collection) ? collection : "";
  return (
    <div className="admin-collection-nav">
      <div className="segmented admin-primary-nav">
        {primaryCollections.map((name) => (
          <button key={name} className={collection === name ? "active" : ""} onClick={() => onSwitch(name)}>
            {name === "reports-attendance" ? <BarChart3 size={16} /> : null}
            {adminCollectionLabel(name)}
          </button>
        ))}
      </div>
      <label className="admin-setup-select">
        <span>Setup</span>
        <select value={setupValue} onChange={(event) => event.target.value && onSwitch(event.target.value)}>
          <option value="">Select setup area</option>
          {setupCollections.map((name) => (
            <option key={name} value={name}>
              {adminCollectionLabel(name)}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function AdminAttendanceReportPanel({
  schoolSlug,
  terms,
  classes,
  members,
  setError,
  onOpenStudentProfile
}: {
  schoolSlug: string;
  terms: Term[];
  classes: ClassItem[];
  members: StaffMember[];
  setError: (message: string) => void;
  onOpenStudentProfile: (studentId?: string) => void;
}) {
  const defaultTerm = useMemo(() => defaultReportTerm(terms), [terms]);
  const [filters, setFilters] = useState({ termId: "", classId: "", coachKey: "", from: "", to: "", status: "all" });
  const [report, setReport] = useState<AttendanceReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState("");
  const selectedClass = report?.classes.find((item) => item.classId === selectedClassId) ?? null;
  const filteredClasses = classes.filter((klass) => !filters.termId || klass.termId === filters.termId);
  const coachMembers = members.filter((member) => member.role === "coach");

  useEffect(() => {
    if (!defaultTerm || filters.termId) return;
    setFilters((current) => ({
      ...current,
      termId: defaultTerm.id,
      from: defaultTerm.startsAt,
      to: defaultTerm.endsAt
    }));
  }, [defaultTerm, filters.termId]);

  useEffect(() => {
    void loadReport();
  }, [schoolSlug, filters.termId, filters.classId, filters.coachKey, filters.from, filters.to, filters.status]);

  async function loadReport() {
    setIsLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });
      const result = await api<AttendanceReportResponse>(`/admin/schools/${schoolSlug}/reports/attendance?${params.toString()}`);
      setReport(result);
      setSelectedClassId((current) => (current && result.classes.some((item) => item.classId === current) ? current : ""));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load attendance report");
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }

  function updateTerm(termId: string) {
    const term = terms.find((item) => item.id === termId);
    setFilters((current) => ({
      ...current,
      termId,
      classId: "",
      from: term?.startsAt ?? current.from,
      to: term?.endsAt ?? current.to
    }));
  }

  if (selectedClass) {
    return (
      <section className="panel attendance-report-panel">
        <div className="coach-detail-header">
          <button type="button" onClick={() => setSelectedClassId("")}>
            <ArrowLeft size={16} />
            Back to report
          </button>
          <div>
            <p className="eyebrow">Attendance report</p>
            <h2>{selectedClass.className}</h2>
            <p className="muted">
              {selectedClass.termName || "Term not recorded"} · {selectedClass.coachName || "Coach not assigned"} · {selectedClass.schedule || "Schedule not recorded"}
            </p>
          </div>
          <span className={`status-pill ${selectedClass.incompleteSessions > 0 ? "unpaid" : "paid"}`}>
            {selectedClass.incompleteSessions > 0 ? `${selectedClass.incompleteSessions} incomplete` : "Complete"}
          </span>
        </div>
        <div className="attendance-summary-grid">
          <div><span>Attendance</span><strong>{formatReportPercent(selectedClass.attendanceRate)}</strong></div>
          <div><span>Present</span><strong>{selectedClass.present}</strong></div>
          <div><span>Absent</span><strong>{selectedClass.absent}</strong></div>
          <div><span>Leave</span><strong>{selectedClass.leave}</strong></div>
        </div>
        <div className="attendance-report-table">
          <div className="attendance-student-report-row attendance-report-heading" aria-hidden="true">
            <span>Student</span>
            <span>Present</span>
            <span>Absent</span>
            <span>Leave</span>
            <span>Attendance</span>
            <span>Last absence</span>
          </div>
          {selectedClass.students.map((student) => (
            <div className="attendance-student-report-row" key={student.registrationId}>
              <button type="button" className="link-button" onClick={() => onOpenStudentProfile(student.studentId)}>
                {student.studentName}
              </button>
              <span>{student.present}</span>
              <span>{student.absent}</span>
              <span>{student.leave}</span>
              <strong>{formatReportPercent(student.attendanceRate)}</strong>
              <span>{formatReportDate(student.lastAbsence) || "None"}</span>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="panel attendance-report-panel">
      <div className="admin-list-header">
        <div>
          <h2>Attendance reports</h2>
          <p className="muted">Review term class attendance and incomplete coach marking.</p>
        </div>
        <span className="status-pill">{isLoading ? "Loading" : `${report?.classes.length ?? 0} classes`}</span>
      </div>
      <div className="attendance-report-filters">
        <label>
          <span>Term</span>
          <select value={filters.termId} onChange={(event) => updateTerm(event.target.value)}>
            <option value="">All terms</option>
            {terms.map((term) => (
              <option key={term.id} value={term.id}>{term.name}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Class</span>
          <select value={filters.classId} onChange={(event) => setFilters((current) => ({ ...current, classId: event.target.value }))}>
            <option value="">All classes</option>
            {filteredClasses.map((klass) => (
              <option key={klass.id} value={klass.id}>{klass.name}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Coach</span>
          <select value={filters.coachKey} onChange={(event) => setFilters((current) => ({ ...current, coachKey: event.target.value }))}>
            <option value="">All coaches</option>
            {coachMembers.map((member) => (
              <option key={member.userKey ?? member.phone ?? member.email} value={member.userKey ?? member.phone ?? member.email ?? ""}>
                {member.name || member.email || member.phone}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>From</span>
          <input type="date" value={filters.from} onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))} />
        </label>
        <label>
          <span>To</span>
          <input type="date" value={filters.to} onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))} />
        </label>
        <label>
          <span>Status</span>
          <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
            <option value="all">All</option>
            <option value="low">Low attendance</option>
            <option value="incomplete">Incomplete attendance</option>
          </select>
        </label>
      </div>
      {report ? (
        <>
          <div className="attendance-summary-grid">
            <div><span>Attendance</span><strong>{formatReportPercent(report.totals.attendanceRate)}</strong></div>
            <div><span>Classes</span><strong>{report.totals.classes}</strong></div>
            <div><span>Sessions marked</span><strong>{report.totals.completedSessions} / {report.totals.expectedSessions}</strong></div>
            <div><span>Incomplete</span><strong>{report.totals.incompleteSessions}</strong></div>
          </div>
          <div className="attendance-report-table">
            <div className="attendance-class-report-row attendance-report-heading" aria-hidden="true">
              <span>Class</span>
              <span>Term</span>
              <span>Coach</span>
              <span>Sessions</span>
              <span>Students</span>
              <span>Attendance</span>
              <span>Missing</span>
            </div>
            {report.classes.length === 0 ? <p className="muted">No classes match these filters.</p> : null}
            {report.classes.map((item) => (
              <article className="attendance-class-report-row" key={item.classId}>
                <strong title={item.className}>{item.className}</strong>
                <span title={item.termName}>{item.termName || "Not recorded"}</span>
                <span title={item.coachName}>{item.coachName || "Not assigned"}</span>
                <span>{item.completedSessions} / {item.expectedSessions} marked</span>
                <span>{item.studentCount}</span>
                <strong>{formatReportPercent(item.attendanceRate)}</strong>
                <span>{item.incompleteSessions} sessions</span>
                <button type="button" onClick={() => setSelectedClassId(item.classId)}>
                  Open
                </button>
              </article>
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}

function AdminClassesPanel({
  items,
  terms,
  members,
  onCreate,
  onEdit,
  onDuplicate
}: {
  items: Record<string, unknown>[];
  terms: Term[];
  members: StaffMember[];
  onCreate: () => void;
  onEdit: (item: Record<string, unknown>) => void;
  onDuplicate: (item: Record<string, unknown>) => void;
}) {
  const [termId, setTermId] = useState("");
  const [status, setStatus] = useState("active-upcoming");
  const [search, setSearch] = useState("");
  const termById = useMemo(() => new Map(terms.map((term) => [term.id, term])), [terms]);
  const coachByKey = useMemo(() => new Map(members.map((member) => [comparableStaffKey(String(member.userKey || member.phone || member.email || "")), member])), [members]);
  const filteredItems = items.filter((item) => {
    const itemTerm = termById.get(String(item.termId ?? ""));
    const itemStatus = classLifecycleStatus(item, itemTerm);
    const matchesTerm = !termId || item.termId === termId;
    const matchesStatus =
      status === "all" ||
      (status === "active-upcoming" && (itemStatus === "active" || itemStatus === "upcoming")) ||
      itemStatus === status;
    const haystack = [
      item.name,
      item.schedule,
      item.templateName,
      item.level,
      item.ageGroup,
      itemTerm?.name,
      coachByKey.get(comparableStaffKey(String(item.coachPhone ?? "")))?.name
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return matchesTerm && matchesStatus && (!search.trim() || haystack.includes(search.trim().toLowerCase()));
  });

  return (
    <section className="panel admin-list-panel classes-panel">
      <div className="admin-list-header">
        <div>
          <h2>Existing Classes</h2>
          <p className="muted">{filteredItems.length} class{filteredItems.length === 1 ? "" : "es"} shown</p>
        </div>
        <button className="primary" type="button" onClick={onCreate}>
          <LayoutDashboard size={16} />
          Create New Class
        </button>
      </div>
      <div className="admin-filter-bar class-filter-bar">
        <label>
          <span>Search</span>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Class, template, coach" />
        </label>
        <label>
          <span>Term</span>
          <select value={termId} onChange={(event) => setTermId(event.target.value)}>
            <option value="">All terms</option>
            {terms.map((term) => (
              <option value={term.id} key={term.id}>{term.name}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="active-upcoming">Active & upcoming</option>
            <option value="active">Active</option>
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
            <option value="all">All</option>
          </select>
        </label>
      </div>
      <div className="classes-table">
        <div className="classes-row classes-heading" aria-hidden="true">
          <span>Class</span>
          <span>Term</span>
          <span>Schedule</span>
          <span>Coach</span>
          <span>Price</span>
          <span>Status</span>
          <span>Actions</span>
        </div>
        {filteredItems.length === 0 ? <p className="muted">No classes match the current filters.</p> : null}
        {filteredItems.map((item) => {
          const itemTerm = termById.get(String(item.termId ?? ""));
          const itemStatus = classLifecycleStatus(item, itemTerm);
          const coach = coachByKey.get(comparableStaffKey(String(item.coachPhone ?? "")));
          return (
            <article className="classes-row" key={adminItemKey(item)}>
              <div>
                <strong>{String(item.name ?? "Untitled class")}</strong>
                <small>{String(item.templateName || item.level || item.ageGroup || "No template")}</small>
              </div>
              <span>{itemTerm?.name || String(item.termId ?? "No term")}</span>
              <span>{String(item.schedule || "No schedule")}</span>
              <span>{coach?.name || coach?.email || String(item.coachPhone || "Not assigned")}</span>
              <strong>{formatAud(Number(item.priceCents ?? 0))}</strong>
              <span className={`status-pill ${itemStatus === "past" ? "unpaid" : "paid"}`}>{itemStatus}</span>
              <div className="admin-row-actions">
                {itemStatus === "past" ? <span className="muted">View only</span> : <button type="button" onClick={() => onEdit(item)}>Edit</button>}
                <button type="button" onClick={() => onDuplicate(item)}>Duplicate</button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function classLifecycleStatus(item: Record<string, unknown>, term: Term | undefined) {
  const today = formatInputDate(new Date());
  const startsAt = String(item.startsAt || term?.startsAt || "");
  const endsAt = String(term?.endsAt || "");
  if (endsAt && endsAt < today) return "past";
  if (startsAt && startsAt > today) return "upcoming";
  return "active";
}

function reEnrollmentPreviewHelp({
  sourceTermId,
  targetTermId,
  selectedCount,
  sourceClassCount,
  targetClassCount
}: {
  sourceTermId: string;
  targetTermId: string;
  selectedCount: number;
  sourceClassCount: number;
  targetClassCount: number;
}) {
  if (!sourceTermId || !targetTermId) return "Select a source term and a target term before previewing.";
  if (sourceTermId === targetTermId) return "Choose a different target term. Re-enrolment creates new records for the next term.";
  if (sourceClassCount === 0) return "There are no source classes in the selected source term.";
  if (selectedCount === 0) return "Select at least one source class, or use all source classes.";
  if (targetClassCount === 0) return "The target term has no classes yet. Preview can show source students, but create or duplicate target classes before creating registrations.";
  return "";
}

interface ReEnrollmentPreviewRow {
  sourceRegistrationId: string;
  studentId: string;
  studentName: string;
  parentName: string;
  guardianEmail: string;
  sourceClassId: string;
  sourceClassName: string;
  sourceSchedule: string;
  targetClassId: string;
  targetClassName: string;
  targetSchedule: string;
  priceCents: number;
  capacityStatus: string;
  targetCapacity: number;
  projectedCount: number;
  status: string;
  include?: boolean;
}

interface ReEnrollmentPreviewResponse {
  rows: ReEnrollmentPreviewRow[];
  summary: {
    total: number;
    ready: number;
    needsReview: number;
    overCapacity: number;
  };
}

function AdminReEnrollmentPanel({
  schoolSlug,
  terms,
  classes,
  setError,
  setNotice,
  onCreated
}: {
  schoolSlug: string;
  terms: Term[];
  classes: ClassItem[];
  setError: (message: string) => void;
  setNotice: (message: string) => void;
  onCreated: () => Promise<void>;
}) {
  const sortedTerms = useMemo(() => [...terms].sort((a, b) => b.startsAt.localeCompare(a.startsAt)), [terms]);
  const defaultSourceTerm = defaultReportTerm(terms) ?? sortedTerms[0];
  const defaultTargetTerm = sortedTerms.find((term) => defaultSourceTerm && term.startsAt > defaultSourceTerm.startsAt) ?? sortedTerms[0];
  const [sourceTermId, setSourceTermId] = useState(defaultSourceTerm?.id ?? "");
  const [targetTermId, setTargetTermId] = useState(defaultTargetTerm?.id ?? "");
  const [selectedSourceClassIds, setSelectedSourceClassIds] = useState<string[]>([]);
  const [rows, setRows] = useState<ReEnrollmentPreviewRow[]>([]);
  const [summary, setSummary] = useState<ReEnrollmentPreviewResponse["summary"] | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [sendEmails, setSendEmails] = useState(false);
  const sourceClasses = classes.filter((klass) => !sourceTermId || klass.termId === sourceTermId);
  const targetClasses = classes.filter((klass) => !targetTermId || klass.termId === targetTermId);
  const sourceClassIdsKey = sourceClasses.map((klass) => klass.id).join(",");
  const selectedCount = selectedSourceClassIds.length;
  const includedRows = rows.filter((row) => row.include !== false && row.targetClassId);
  const previewDisabled = isPreviewing || !sourceTermId || !targetTermId || sourceTermId === targetTermId || selectedCount === 0;
  const previewHelp = reEnrollmentPreviewHelp({
    sourceTermId,
    targetTermId,
    selectedCount,
    sourceClassCount: sourceClasses.length,
    targetClassCount: targetClasses.length
  });
  const filteredRows = rows.filter((row) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "ready") return row.status === "ready";
    if (statusFilter === "review") return row.status !== "ready";
    if (statusFilter === "over") return row.capacityStatus === "over";
    return true;
  });

  useEffect(() => {
    setSelectedSourceClassIds(sourceClasses.map((klass) => klass.id));
    setRows([]);
    setSummary(null);
  }, [sourceTermId, targetTermId, sourceClassIdsKey]);

  useEffect(() => {
    if (!sourceTermId && defaultSourceTerm?.id) setSourceTermId(defaultSourceTerm.id);
    if (!targetTermId && defaultTargetTerm?.id) setTargetTermId(defaultTargetTerm.id);
  }, [defaultSourceTerm?.id, defaultTargetTerm?.id, sourceTermId, targetTermId]);

  function toggleSourceClass(classId: string) {
    setSelectedSourceClassIds((current) => {
      return current.includes(classId) ? current.filter((id) => id !== classId) : [...current, classId];
    });
  }

  function selectAllSourceClasses() {
    setSelectedSourceClassIds(sourceClasses.map((klass) => klass.id));
  }

  function unselectAllSourceClasses() {
    setSelectedSourceClassIds([]);
  }

  async function preview() {
    if (!sourceTermId || !targetTermId) {
      setError("Select source and target terms.");
      return;
    }
    if (sourceTermId === targetTermId) {
      setError("Source term and target term must be different.");
      return;
    }
    setIsPreviewing(true);
    setError("");
    try {
      const result = await api<ReEnrollmentPreviewResponse>(`/admin/schools/${schoolSlug}/re-enrolments/preview`, {
        method: "POST",
        body: JSON.stringify({
          sourceTermId,
          targetTermId,
          sourceClassIds: selectedSourceClassIds
        })
      });
      setRows(result.rows.map((row) => ({ ...row, include: row.status === "ready" })));
      setSummary(result.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to preview re-enrolment");
    } finally {
      setIsPreviewing(false);
    }
  }

  function updateRow(sourceRegistrationId: string, patch: Partial<ReEnrollmentPreviewRow>) {
    setRows((current) =>
      current.map((row) => {
        if (row.sourceRegistrationId !== sourceRegistrationId) return row;
        const nextTargetClass = patch.targetClassId ? targetClasses.find((klass) => klass.id === patch.targetClassId) : undefined;
        return {
          ...row,
          ...patch,
          ...(nextTargetClass
            ? {
                targetClassName: nextTargetClass.name,
                targetSchedule: nextTargetClass.schedule,
                priceCents: Number(nextTargetClass.priceCents ?? row.priceCents),
                status: "ready",
                capacityStatus: "ok"
              }
            : {})
        };
      })
    );
  }

  async function createCampaign() {
    if (includedRows.length === 0) {
      setError("Select at least one ready student.");
      return;
    }
    const overRows = includedRows.filter((row) => row.capacityStatus === "over");
    if (overRows.length > 0 && !window.confirm(`${overRows.length} selected row(s) are over capacity. Continue and create waitlist registrations?`)) return;
    if (!window.confirm(`Create ${includedRows.length} re-enrolment registration${includedRows.length === 1 ? "" : "s"}${sendEmails ? " and send email links" : " with payment links"}?`)) return;
    setIsCreating(true);
    setError("");
    try {
      const result = await api<{ createdRegistrationCount: number; skippedCount: number; emailCount: number }>(`/admin/schools/${schoolSlug}/re-enrolments`, {
        method: "POST",
        body: JSON.stringify({
          sourceTermId,
          targetTermId,
          createPaymentLinks: true,
          sendEmails,
          paymentBaseUrl: `${window.location.origin}/${schoolSlug}`,
          rows: includedRows.map((row) => ({
            sourceRegistrationId: row.sourceRegistrationId,
            targetClassId: row.targetClassId,
            priceCents: row.priceCents,
            include: row.include !== false
          }))
        })
      });
      setNotice(`Created ${result.createdRegistrationCount} registration${result.createdRegistrationCount === 1 ? "" : "s"}${result.skippedCount ? `, skipped ${result.skippedCount} duplicate${result.skippedCount === 1 ? "" : "s"}` : ""}${sendEmails ? `, sent ${result.emailCount} email${result.emailCount === 1 ? "" : "s"}` : ""}.`);
      setRows([]);
      setSummary(null);
      await onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to create re-enrolment");
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <section className="panel re-enrolment-panel">
      <div className="admin-list-header">
        <div>
          <h2>Re-enrolment</h2>
          <p className="muted">Move students from one term into the next term in bulk, using class template progression where available.</p>
        </div>
        <span className="status-pill">{rows.length ? `${includedRows.length} selected` : "Draft first"}</span>
      </div>
      <div className="re-enrolment-controls">
        <label>
          <span>Source term</span>
          <select value={sourceTermId} onChange={(event) => setSourceTermId(event.target.value)}>
            <option value="">Select source term</option>
            {sortedTerms.map((term) => (
              <option value={term.id} key={term.id}>{term.name}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Target term</span>
          <select value={targetTermId} onChange={(event) => setTargetTermId(event.target.value)}>
            <option value="">Select target term</option>
            {sortedTerms.map((term) => (
              <option value={term.id} key={term.id}>{term.name}</option>
            ))}
          </select>
        </label>
      </div>
      <section className="re-enrolment-class-picker">
        <div className="split-heading">
          <div>
            <h3>Source classes</h3>
            <p className="muted">{selectedSourceClassIds.length} selected</p>
          </div>
          <div className="inline">
            <button type="button" onClick={selectAllSourceClasses}>Select all</button>
            <button type="button" onClick={unselectAllSourceClasses}>Unselect all</button>
          </div>
        </div>
        <div className="notification-class-list">
          {sourceClasses.length === 0 ? <p className="muted">No source classes in this term.</p> : null}
          {sourceClasses.map((klass) => (
            <label className="notification-class-option" key={klass.id}>
              <input type="checkbox" checked={selectedSourceClassIds.includes(klass.id)} onChange={() => toggleSourceClass(klass.id)} />
              <span>
                <strong>{klass.name}</strong>
                <small>{klass.schedule || "Schedule not recorded"}</small>
              </span>
            </label>
          ))}
        </div>
      </section>
      <div className="inline form-actions">
        <button className="primary" type="button" onClick={preview} disabled={previewDisabled}>
          {isPreviewing ? "Previewing..." : "Preview re-enrolment"}
        </button>
      </div>
      {previewHelp ? <p className="muted re-enrolment-help">{previewHelp}</p> : null}
      {summary ? (
        <>
          <div className="notification-preview">
            <div><span>Total</span><strong>{summary.total}</strong></div>
            <div><span>Ready</span><strong>{summary.ready}</strong></div>
            <div><span>Needs review</span><strong>{summary.needsReview}</strong></div>
            <div><span>Over capacity</span><strong>{summary.overCapacity}</strong></div>
          </div>
          <div className="admin-filter-bar">
            <label>
              <span>Status</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="all">All</option>
                <option value="ready">Ready</option>
                <option value="review">Needs review</option>
                <option value="over">Over capacity</option>
              </select>
            </label>
            <label className="open-toggle">
              <input type="checkbox" checked={sendEmails} onChange={(event) => setSendEmails(event.target.checked)} />
              Send email links now
            </label>
          </div>
          <div className="re-enrolment-table">
            <div className="re-enrolment-row re-enrolment-heading" aria-hidden="true">
              <span>Use</span>
              <span>Student</span>
              <span>Current class</span>
              <span>Target class</span>
              <span>Price</span>
              <span>Status</span>
            </div>
            {filteredRows.map((row) => (
              <article className="re-enrolment-row" key={row.sourceRegistrationId}>
                <label className="open-toggle">
                  <input type="checkbox" checked={row.include !== false} onChange={(event) => updateRow(row.sourceRegistrationId, { include: event.target.checked })} />
                </label>
                <div>
                  <strong>{row.studentName}</strong>
                  <small>{row.parentName || row.guardianEmail || "Parent not recorded"}</small>
                </div>
                <div>
                  <strong>{row.sourceClassName}</strong>
                  <small>{row.sourceSchedule || "Schedule not recorded"}</small>
                </div>
                <select value={row.targetClassId} onChange={(event) => updateRow(row.sourceRegistrationId, { targetClassId: event.target.value })}>
                  <option value="">Needs review</option>
                  {targetClasses.map((klass) => (
                    <option value={klass.id} key={klass.id}>
                      {klass.name}{klass.schedule ? ` · ${klass.schedule}` : ""}
                    </option>
                  ))}
                </select>
                <input
                  aria-label={`Price for ${row.studentName}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={(row.priceCents / 100).toFixed(2)}
                  onChange={(event) => updateRow(row.sourceRegistrationId, { priceCents: Math.round(Number(event.target.value || 0) * 100) })}
                />
                <span className={`status-pill ${row.status === "ready" && row.capacityStatus !== "over" ? "paid" : "unpaid"}`}>
                  {row.capacityStatus === "over" ? "Over capacity" : row.status === "ready" ? "Ready" : "Needs review"}
                </span>
              </article>
            ))}
          </div>
          <div className="inline form-actions">
            <button className="primary" type="button" onClick={createCampaign} disabled={isCreating || includedRows.length === 0}>
              {isCreating ? "Creating..." : `Create ${includedRows.length} registrations`}
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}

type NotificationAudienceType = "single-class" | "multiple-classes" | "term";

const notificationVariables = [
  { label: "Student name", value: "{{studentName}}" },
  { label: "Student age", value: "{{studentAge}}" },
  { label: "Parent name", value: "{{parentName}}" },
  { label: "Class name", value: "{{className}}" },
  { label: "Class schedule", value: "{{classSchedule}}" },
  { label: "Venue name", value: "{{venueName}}" },
  { label: "Venue address", value: "{{venueAddress}}" },
  { label: "Term name", value: "{{termName}}" },
  { label: "Term start date", value: "{{termStartDate}}" },
  { label: "Term end date", value: "{{termEndDate}}" },
  { label: "School name", value: "{{schoolName}}" },
  { label: "Contact email", value: "{{contactEmail}}" }
];

interface NotificationAudiencePreview {
  audience: {
    audienceType: string;
    termId: string;
    classIds: string[];
    classNames: string[];
    registrationCount: number;
    recipientCount: number;
    uniqueParentEmailCount: number;
    missingEmailCount: number;
  };
}

function AdminNotificationComposer({
  schoolSlug,
  terms,
  classes,
  initialItem,
  setError,
  setNotice,
  onCancel,
  onSent
}: {
  schoolSlug: string;
  terms: Term[];
  classes: ClassItem[];
  initialItem: Record<string, unknown> | null;
  setError: (message: string) => void;
  setNotice: (message: string) => void;
  onCancel: () => void;
  onSent: () => Promise<void>;
}) {
  const initialClassIds = notificationClassIds(initialItem);
  const initialAudienceType = notificationAudienceType(initialItem, initialClassIds);
  const defaultTerm = defaultReportTerm(terms);
  const [audienceType, setAudienceType] = useState<NotificationAudienceType>(initialAudienceType);
  const [termId, setTermId] = useState(String(initialItem?.termId ?? defaultTerm?.id ?? ""));
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>(initialClassIds);
  const [subject, setSubject] = useState(String(initialItem?.subject ?? ""));
  const [message, setMessage] = useState(String(initialItem?.message ?? defaultNotificationMessage()));
  const [preview, setPreview] = useState<NotificationAudiencePreview["audience"] | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const filteredClasses = classes.filter((klass) => !termId || klass.termId === termId);

  useEffect(() => {
    let cancelled = false;
    async function loadPreview() {
      const payload = notificationAudiencePayload(audienceType, termId, selectedClassIds);
      if (!payload) {
        setPreview(null);
        return;
      }
      setIsPreviewLoading(true);
      try {
        const result = await api<NotificationAudiencePreview>(`/admin/schools/${schoolSlug}/notifications/preview`, {
          method: "POST",
          body: JSON.stringify(payload)
        });
        if (!cancelled) setPreview(result.audience);
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setIsPreviewLoading(false);
      }
    }
    void loadPreview();
    return () => {
      cancelled = true;
    };
  }, [audienceType, termId, selectedClassIds.join(","), schoolSlug]);

  function toggleClass(classId: string) {
    setSelectedClassIds((current) => (current.includes(classId) ? current.filter((id) => id !== classId) : [...current, classId]));
  }

  function selectAllShown() {
    const shownIds = filteredClasses.map((klass) => klass.id);
    setSelectedClassIds((current) => [...new Set([...current, ...shownIds])]);
  }

  function clearShown() {
    const shownIds = new Set(filteredClasses.map((klass) => klass.id));
    setSelectedClassIds((current) => current.filter((id) => !shownIds.has(id)));
  }

  function insertVariable(value: string) {
    if (!value) return;
    setMessage((current) => `${current}${current && !current.endsWith(" ") && !current.endsWith("\n") ? " " : ""}${value}`);
  }

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const audiencePayload = notificationAudiencePayload(audienceType, termId, selectedClassIds);
    if (!audiencePayload) {
      setError("Select a notification audience.");
      return;
    }
    if (!subject.trim() || !message.trim()) {
      setError("Subject and message are required.");
      return;
    }
    const recipientCount = preview?.recipientCount ?? 0;
    if (recipientCount === 0) {
      setError("No parent emails found for this audience.");
      return;
    }
    if (!window.confirm(`Send ${recipientCount} email${recipientCount === 1 ? "" : "s"} for ${preview?.uniqueParentEmailCount ?? recipientCount} unique parent email${(preview?.uniqueParentEmailCount ?? recipientCount) === 1 ? "" : "s"}?`)) return;
    setIsSending(true);
    setError("");
    try {
      const result = await api<{ item: Record<string, unknown> }>(`/admin/schools/${schoolSlug}/notifications`, {
        method: "POST",
        body: JSON.stringify({ ...audiencePayload, subject, message })
      });
      const emailCount = Number(result.item.emailCount ?? result.item.recipientCount ?? result.item.sentCount ?? recipientCount);
      setNotice(`Notification sent as ${emailCount} email${emailCount === 1 ? "" : "s"}.`);
      await onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to send notification");
    } finally {
      setIsSending(false);
    }
  }

  return (
    <form className="panel admin-form-page notification-composer" onSubmit={send}>
      <div className="admin-form-header">
        <button type="button" onClick={onCancel}>
          <ArrowLeft size={16} />
          Back to Notifications
        </button>
        <div>
          <p className="eyebrow">Notifications</p>
          <h2>Send announcement</h2>
          <p className="muted">Choose an audience, review the recipient count, then send by email.</p>
        </div>
      </div>
      <div>
        <label>Audience</label>
        <div className="segmented compact-segmented notification-audience-tabs">
          {[
            { value: "single-class", label: "Single class" },
            { value: "multiple-classes", label: "Multiple classes" },
            { value: "term", label: "Whole term" }
          ].map((option) => (
            <button type="button" key={option.value} className={audienceType === option.value ? "active" : ""} onClick={() => setAudienceType(option.value as NotificationAudienceType)}>
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {audienceType === "term" || audienceType === "multiple-classes" ? (
        <div>
          <label>Term</label>
          <select value={termId} onChange={(event) => setTermId(event.target.value)}>
            <option value="">All terms</option>
            {terms.map((term) => (
              <option key={term.id} value={term.id}>{term.name}</option>
            ))}
          </select>
        </div>
      ) : null}
      {audienceType === "single-class" ? (
        <div>
          <label>Class</label>
          <select value={selectedClassIds[0] ?? ""} onChange={(event) => setSelectedClassIds(event.target.value ? [event.target.value] : [])} required>
            <option value="" disabled>Select class</option>
            {classes.map((klass) => (
              <option value={klass.id} key={klass.id}>
                {klass.name}{klass.schedule ? ` · ${klass.schedule}` : ""}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {audienceType === "multiple-classes" ? (
        <section className="notification-class-picker">
          <div className="split-heading">
            <div>
              <h3>Classes</h3>
              <p className="muted">{selectedClassIds.length} selected</p>
            </div>
            <div className="inline">
              <button type="button" onClick={selectAllShown}>Select all shown</button>
              <button type="button" onClick={clearShown}>Clear shown</button>
            </div>
          </div>
          <div className="notification-class-list">
            {filteredClasses.map((klass) => (
              <label className="notification-class-option" key={klass.id}>
                <input type="checkbox" checked={selectedClassIds.includes(klass.id)} onChange={() => toggleClass(klass.id)} />
                <span>
                  <strong>{klass.name}</strong>
                  <small>{klass.schedule || "Schedule not recorded"}</small>
                </span>
              </label>
            ))}
          </div>
        </section>
      ) : null}
      <div className="notification-preview">
        <div><span>Classes</span><strong>{isPreviewLoading ? "..." : preview?.classIds.length ?? 0}</strong></div>
        <div><span>Registrations</span><strong>{isPreviewLoading ? "..." : preview?.registrationCount ?? 0}</strong></div>
        <div><span>Emails to send</span><strong>{isPreviewLoading ? "..." : preview?.recipientCount ?? 0}</strong></div>
        <div><span>Unique parents</span><strong>{isPreviewLoading ? "..." : preview?.uniqueParentEmailCount ?? 0}</strong></div>
        <div><span>Missing email</span><strong>{isPreviewLoading ? "..." : preview?.missingEmailCount ?? 0}</strong></div>
      </div>
      <label>Subject</label>
      <input value={subject} onChange={(event) => setSubject(event.target.value)} required />
      <div className="notification-message-toolbar">
        <label>Message</label>
        <select value="" onChange={(event) => insertVariable(event.target.value)} aria-label="Insert notification variable">
          <option value="">Insert variable</option>
          {notificationVariables.map((variable) => (
            <option key={variable.value} value={variable.value}>
              {variable.label}
            </option>
          ))}
        </select>
      </div>
      <textarea rows={7} value={message} onChange={(event) => setMessage(event.target.value)} required />
      <p className="muted">Only the text you write is sent. Variables work in both subject and message and are replaced for each student registration.</p>
      <div className="inline form-actions">
        <button type="button" onClick={onCancel}>Cancel</button>
        <button className="primary" type="submit" disabled={isSending || isPreviewLoading}>
          <Mail size={16} />
          {isSending ? "Sending..." : "Send announcement"}
        </button>
      </div>
    </form>
  );
}

function AdminNotificationsPanel({
  items,
  onCreate,
  onView,
  onDuplicate
}: {
  items: Record<string, unknown>[];
  onCreate: () => void;
  onView: (item: Record<string, unknown>) => void;
  onDuplicate: (item: Record<string, unknown>) => void;
}) {
  return (
    <section className="panel admin-list-panel">
      <div className="admin-list-header">
        <div>
          <h2>Sent notifications</h2>
          <p className="muted">{items.length} sent announcement{items.length === 1 ? "" : "s"}</p>
        </div>
        <button className="primary" type="button" onClick={onCreate}>
          <Mail size={16} />
          Send announcement
        </button>
      </div>
      <div className="table-list">
        {items.length === 0 ? <p className="muted">No notifications sent yet.</p> : null}
        {items.map((item) => (
          <article className="row-card admin-item" key={adminItemKey(item)}>
            <div>
              <strong>{adminItemTitle("notifications", item)}</strong>
              <span>{adminItemSubtitle("notifications", item)}</span>
            </div>
            <div className="admin-row-actions">
              <button type="button" onClick={() => onView(item)}>View</button>
              <button type="button" onClick={() => onDuplicate(item)}>Duplicate</button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function AdminNotificationSentView({
  item,
  onBack,
  onDuplicate
}: {
  item: Record<string, unknown>;
  onBack: () => void;
  onDuplicate: () => void;
}) {
  return (
    <section className="panel admin-form-page notification-sent-view">
      <div className="admin-form-header">
        <button type="button" onClick={onBack}>
          <ArrowLeft size={16} />
          Back to Notifications
        </button>
        <div>
          <p className="eyebrow">Sent notification</p>
          <h2>{String(item.subject ?? "Notification")}</h2>
          <p className="muted">{notificationAudienceLabel(item)} · sent {formatDateTime(String(item.sentAt ?? item.createdAt ?? ""))}</p>
        </div>
      </div>
      <div className="notification-preview">
        <div><span>Classes</span><strong>{notificationClassIds(item).length || 1}</strong></div>
        <div><span>Registrations</span><strong>{String(item.registrationCount ?? "-")}</strong></div>
        <div><span>Emails sent</span><strong>{String(item.emailCount ?? item.recipientCount ?? item.sentCount ?? "-")}</strong></div>
        <div><span>Unique parents</span><strong>{String(item.uniqueParentEmailCount ?? "-")}</strong></div>
        <div><span>Missing email</span><strong>{String(item.missingEmailCount ?? 0)}</strong></div>
      </div>
      <div className="notification-message-preview">
        <label>Audience</label>
        <p>{notificationClassNames(item).join(", ") || String(item.className ?? item.classId ?? "No class recorded")}</p>
        <label>Message</label>
        <pre>{String(item.message ?? "")}</pre>
      </div>
      <div className="inline form-actions">
        <button type="button" onClick={onBack}>Back</button>
        <button className="primary" type="button" onClick={onDuplicate}>Duplicate</button>
      </div>
    </section>
  );
}

function AdminPaymentEmailSettingsPanel({
  schoolSlug,
  setError,
  setNotice,
  refresh
}: {
  schoolSlug: string;
  setError: (message: string) => void;
  setNotice: (message: string) => void;
  refresh: () => Promise<void>;
}) {
  const [settings, setSettings] = useState<PaymentSettingsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    void loadSettings();
  }, [schoolSlug]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_connect") !== "return") return;
    const error = params.get("error");
    if (error) {
      setError(params.get("error_description") || "Stripe connection was cancelled.");
      window.history.replaceState(null, "", `/${schoolSlug}`);
      return;
    }
    const code = params.get("code");
    const state = params.get("state");
    if (code && state) void completeStripeConnect(code, state);
  }, [schoolSlug]);

  async function loadSettings() {
    setIsLoading(true);
    setError("");
    try {
      const result = await api<PaymentSettingsResponse>(`/admin/schools/${schoolSlug}/payments/settings`);
      setSettings(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load payment settings");
    } finally {
      setIsLoading(false);
    }
  }

  async function connectStripe() {
    if (settings && !settings.payment.stripeConnectReady) {
      setError("Stripe Connect is not configured for the platform yet. Add STRIPE_SECRET_KEY and STRIPE_CONNECT_CLIENT_ID to the deployment environment first.");
      return;
    }
    setIsConnecting(true);
    setError("");
    try {
      const result = await api<{ url: string }>(`/admin/schools/${schoolSlug}/payments/stripe/connect-link`, {
        method: "POST",
        body: JSON.stringify({ paymentBaseUrl: `${window.location.origin}/${schoolSlug}` })
      });
      window.location.href = result.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start Stripe connection");
      setIsConnecting(false);
    }
  }

  async function completeStripeConnect(code: string, state: string) {
    setIsConnecting(true);
    setError("");
    try {
      await api(`/admin/schools/${schoolSlug}/payments/stripe/oauth-callback`, {
        method: "POST",
        body: JSON.stringify({ code, state })
      });
      setNotice("Stripe account connected.");
      window.history.replaceState(null, "", `/${schoolSlug}`);
      await loadSettings();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to complete Stripe connection");
    } finally {
      setIsConnecting(false);
    }
  }

  async function savePaymentSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSavingPayment(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api(`/admin/schools/${schoolSlug}/payments/settings`, {
        method: "PUT",
        body: JSON.stringify({
          paymentContactEmail: String(form.get("paymentContactEmail")),
          paymentInstructions: String(form.get("paymentInstructions"))
        })
      });
      setNotice("Payment settings saved.");
      await loadSettings();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save payment settings");
    } finally {
      setIsSavingPayment(false);
    }
  }

  async function saveEmailTemplate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSavingTemplate(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api(`/admin/schools/${schoolSlug}/email-templates/unpaid-reminder`, {
        method: "PUT",
        body: JSON.stringify({
          subject: String(form.get("subject")),
          bodyText: String(form.get("bodyText"))
        })
      });
      setNotice("Email template saved.");
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save email template");
    } finally {
      setIsSavingTemplate(false);
    }
  }

  const payment = settings?.payment;
  const template = settings?.emailTemplates["unpaid-reminder"];

  return (
    <section className="admin-settings-stack">
      <section className="panel admin-settings-panel">
        <div className="admin-list-header">
          <div>
            <h2>Payment settings</h2>
            <p className="muted">Connect the school's Stripe account and manage payment fallback details.</p>
          </div>
          <span className={`status-pill ${payment?.stripeConfigured ? "paid" : "unpaid"}`}>
            {payment?.stripeConfigured ? "Stripe connected" : "Stripe not connected"}
          </span>
        </div>
        {isLoading ? <p className="muted">Loading payment settings...</p> : null}
        {payment ? (
          <>
            <div className="payment-connect-box">
              <div>
                <strong>Stripe Connect</strong>
                <span>{payment.stripeConnectAccountId || "No connected account yet"}</span>
                {!payment.stripeConnectReady ? <small className="muted">Platform Stripe Connect keys are not configured yet.</small> : null}
              </div>
              <button className="primary" type="button" onClick={connectStripe} disabled={isConnecting}>
                <CreditCard size={16} />
                {isConnecting ? "Connecting..." : payment.stripeConfigured ? "Reconnect Stripe" : "Connect Stripe"}
              </button>
            </div>
            <form className="admin-settings-form" onSubmit={savePaymentSettings}>
              <label>Payment contact email</label>
              <input name="paymentContactEmail" type="email" defaultValue={payment.paymentContactEmail ?? ""} />
              <p className="muted">Used in payment emails and shown to parents when online payment is unavailable or they need payment help.</p>
              <label>Payment fallback instructions</label>
              <textarea name="paymentInstructions" rows={3} defaultValue={payment.paymentInstructions ?? ""} placeholder="Shown or used when online payment is not available." />
              <p className="muted">Shown on payment pages when Stripe is not connected, for example bank transfer or contact instructions.</p>
              <div className="inline form-actions">
                <button className="primary" type="submit" disabled={isSavingPayment}>
                  {isSavingPayment ? "Saving..." : "Save payment settings"}
                </button>
              </div>
            </form>
          </>
        ) : null}
      </section>

      <section className="panel admin-settings-panel">
        <div className="panel-title">
          <Mail size={20} />
          <h2>Unpaid reminder email</h2>
        </div>
        {template ? (
          <form className="admin-settings-form" onSubmit={saveEmailTemplate}>
            <label>Subject</label>
            <input name="subject" defaultValue={template.subject} required />
            <label>Body</label>
            <textarea name="bodyText" rows={14} defaultValue={template.bodyText} required />
            <p className="muted">
              Available variables: {"{{schoolName}}, {{parentName}}, {{items}}, {{totalAmount}}, {{paymentLink}}, {{contactEmail}}, {{sentBy}}"}
            </p>
            <div className="inline form-actions">
              <button className="primary" type="submit" disabled={isSavingTemplate}>
                {isSavingTemplate ? "Saving..." : "Save email template"}
              </button>
            </div>
          </form>
        ) : null}
      </section>
    </section>
  );
}

type CoachScheduleRange = "today" | "week" | "upcoming";

interface CoachSession {
  id: string;
  type: "class" | "holiday";
  classId?: string;
  programId?: string;
  blockId?: string;
  date: string;
  startTime: string;
  endTime: string;
  title: string;
  venue: string;
  role: "primary" | "substitute";
  registrations: Registration[];
}

function CoachSchedulePanel({
  schoolSlug,
  registrations,
  classes,
  terms,
  holidayPrograms,
  setError,
  setNotice
}: {
  schoolSlug: string;
  registrations: Registration[];
  classes: ClassItem[];
  terms: Term[];
  holidayPrograms: HolidayProgram[];
  setError: (message: string) => void;
  setNotice: (message: string) => void;
}) {
  const [range, setRange] = useState<CoachScheduleRange>("week");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [sessionView, setSessionView] = useState<"schedule" | "roster" | "attendance">("schedule");
  const classById = useMemo(() => new Map(classes.map((klass) => [klass.id, klass])), [classes]);
  const termById = useMemo(() => new Map(terms.map((term) => [term.id, term])), [terms]);
  const programById = useMemo(() => new Map(holidayPrograms.map((program) => [program.id, program])), [holidayPrograms]);
  const dateRange = useMemo(() => coachScheduleDateRange(range), [range]);
  const sessions = useMemo(
    () => buildCoachSessions(registrations, classById, termById, programById, dateRange),
    [classById, dateRange, programById, registrations, termById]
  );
  const selectedSession = sessions.find((session) => session.id === selectedSessionId);

  useEffect(() => {
    if (sessionView !== "schedule" && (!selectedSessionId || !sessions.some((session) => session.id === selectedSessionId))) {
      setSessionView("schedule");
      setSelectedSessionId("");
    }
  }, [selectedSessionId, sessionView, sessions]);

  if (sessionView !== "schedule" && selectedSession) {
    return (
      <section className="panel coach-schedule-panel coach-detail-panel">
        <div className="coach-detail-header">
          <button type="button" onClick={() => setSessionView("schedule")}>
            <ArrowLeft size={16} />
            Back to schedule
          </button>
          <div>
            <p className="eyebrow">{sessionView === "attendance" ? "Attendance" : "Roster"}</p>
            <h2>{selectedSession.title}</h2>
            <p className="muted">
              {formatCoachSessionDay(selectedSession.date)} · {formatCoachSessionTime(selectedSession.startTime, selectedSession.endTime)} · {selectedSession.venue || "Venue not recorded"}
            </p>
          </div>
          <span className="status-pill">{selectedSession.role}</span>
        </div>
        {sessionView === "roster" ? <CoachSessionRoster session={selectedSession} /> : null}
        {sessionView === "attendance" ? (
          <CoachAttendancePanel
            schoolSlug={schoolSlug}
            session={selectedSession}
            setError={setError}
            setNotice={setNotice}
          />
        ) : null}
      </section>
    );
  }

  return (
    <section className="panel coach-schedule-panel">
      <div className="admin-list-header">
        <div>
          <h2>Coach schedule</h2>
          <p className="muted">{sessions.length} session{sessions.length === 1 ? "" : "s"} shown</p>
        </div>
        <div className="segmented compact-segmented" aria-label="Coach schedule range">
          {[
            { value: "today", label: "Today" },
            { value: "week", label: "This week" },
            { value: "upcoming", label: "All upcoming" }
          ].map((option) => (
            <button
              type="button"
              key={option.value}
              className={range === option.value ? "active" : ""}
              onClick={() => setRange(option.value as CoachScheduleRange)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {sessions.length === 0 ? <p className="muted">No assigned sessions in this range.</p> : null}
      <div className="coach-session-list">
        {sessions.map((session) => (
          <article className="coach-session-row" key={session.id}>
            <div className="coach-session-date">
              <strong>{formatCoachSessionDay(session.date)}</strong>
              <span>{formatCoachSessionTime(session.startTime, session.endTime)}</span>
            </div>
            <div className="coach-session-main">
              <strong>{session.title}</strong>
              <span>{session.venue || "Venue not recorded"}</span>
            </div>
            <span>{session.type === "holiday" ? "Holiday block" : "Term class"}</span>
            <span className="status-pill">{session.role}</span>
            <strong>{uniqueRegistrationCount(session.registrations)} students</strong>
            <button type="button" onClick={() => {
              setSelectedSessionId(session.id);
              setSessionView("roster");
            }}>
              Open roster
            </button>
            <button className="primary" type="button" onClick={() => {
              setSelectedSessionId(session.id);
              setSessionView("attendance");
            }}>
              Take attendance
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

function CoachSessionRoster({ session }: { session: CoachSession }) {
  const registrations = uniqueRegistrations(session.registrations);
  return (
    <section className="coach-session-roster">
      <div className="split-heading">
        <div>
          <h3>Students</h3>
          <p className="muted">{registrations.length} student{registrations.length === 1 ? "" : "s"} in this session</p>
        </div>
      </div>
      <div className="coach-roster-list">
        <div className="coach-roster-row coach-roster-heading" aria-hidden="true">
          <span>Student</span>
          <span>Age</span>
          <span>Parent</span>
          <span>Emergency</span>
          <span>Health</span>
        </div>
        {registrations.map((registration) => (
          <div className="coach-roster-row" key={registration.id}>
            <strong>{registration.student?.name || "Unnamed student"}</strong>
            <span>{studentAgeLabel(registration.student?.dateOfBirth)}</span>
            <span title={registration.parent?.name || registration.guardianPhone || ""}>{registration.parent?.name || registration.guardianPhone || "Not recorded"}</span>
            <span title={`${registration.emergencyContact?.name || ""} ${registration.emergencyContact?.phone || ""}`.trim()}>
              {[registration.emergencyContact?.name, registration.emergencyContact?.phone].filter(Boolean).join(" · ") || "Not recorded"}
            </span>
            <span title={[registration.student?.allergies, registration.student?.healthNotes].filter(Boolean).join(" · ")}>
              {[registration.student?.allergies, registration.student?.healthNotes].filter(Boolean).join(" · ") || "None recorded"}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

type AttendanceStatus = "not_marked" | "present" | "absent" | "leave";

interface AttendanceRecord {
  registrationId: string;
  status: AttendanceStatus;
  note?: string;
}

function CoachAttendancePanel({
  schoolSlug,
  session,
  setError,
  setNotice
}: {
  schoolSlug: string;
  session: CoachSession;
  setError: (message: string) => void;
  setNotice: (message: string) => void;
}) {
  const registrations = uniqueRegistrations(session.registrations);
  const [records, setRecords] = useState<Record<string, AttendanceStatus>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadAttendance() {
      setError("");
      const initialRecords = Object.fromEntries(registrations.map((registration) => [registration.id, "not_marked" as AttendanceStatus]));
      try {
        const result = await api<{ records: AttendanceRecord[] }>(`/coach/schools/${schoolSlug}/attendance?${attendanceQueryString(session)}`);
        if (cancelled) return;
        const nextRecords = { ...initialRecords };
        result.records.forEach((record) => {
          if (record.status === "present" || record.status === "absent" || record.status === "leave") {
            nextRecords[record.registrationId] = record.status;
          }
        });
        setRecords(nextRecords);
        setIsDirty(false);
      } catch (err) {
        if (cancelled) return;
        setRecords(initialRecords);
        setIsDirty(false);
        setError(err instanceof Error ? err.message : "Unable to load attendance");
      }
    }
    void loadAttendance();
    return () => {
      cancelled = true;
    };
  }, [schoolSlug, session.id]);

  const summary = attendanceSummary(registrations, records);

  function updateStatus(registrationId: string, status: AttendanceStatus) {
    setRecords((current) => ({ ...current, [registrationId]: status }));
    setIsDirty(true);
  }

  function markAllPresent() {
    setRecords(Object.fromEntries(registrations.map((registration) => [registration.id, "present" as AttendanceStatus])));
    setIsDirty(true);
  }

  async function saveAttendance() {
    setIsSaving(true);
    setError("");
    try {
      await api(`/coach/schools/${schoolSlug}/attendance`, {
        method: "POST",
        body: JSON.stringify({
          sessionType: session.type,
          classId: session.classId,
          programId: session.programId,
          blockId: session.blockId,
          date: session.date,
          records: registrations
            .map((registration) => ({
              registrationId: registration.id,
              status: records[registration.id] ?? "not_marked"
            }))
            .filter((record) => record.status !== "not_marked")
        })
      });
      setIsDirty(false);
      setNotice("Attendance saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save attendance");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="coach-attendance-panel">
      <div className="split-heading">
        <div>
          <h3>Take attendance</h3>
          <p className="muted">{registrations.length} student{registrations.length === 1 ? "" : "s"} in this session</p>
        </div>
        <div className="inline attendance-actions">
          <button type="button" onClick={markAllPresent} disabled={registrations.length === 0}>
            Mark all present
          </button>
          <button className="primary" type="button" onClick={saveAttendance} disabled={isSaving || registrations.length === 0}>
            {isSaving ? "Saving..." : "Save attendance"}
          </button>
        </div>
      </div>
      <div className="attendance-summary-grid">
        <div><span>Present</span><strong>{summary.present}</strong></div>
        <div><span>Absent</span><strong>{summary.absent}</strong></div>
        <div><span>Leave</span><strong>{summary.leave}</strong></div>
        <div><span>Not marked</span><strong>{summary.not_marked}</strong></div>
      </div>
      {isDirty ? <p className="muted">You have unsaved attendance changes.</p> : null}
      <div className="attendance-list">
        {registrations.map((registration) => {
          const currentStatus = records[registration.id] ?? "not_marked";
          return (
            <article className="attendance-student-row" key={registration.id}>
              <div className="attendance-student-info">
                <strong>{registration.student?.name || "Unnamed student"}</strong>
                <span>{studentAgeSentence(registration.student?.dateOfBirth)}</span>
                <small>
                  Emergency: {[registration.emergencyContact?.name, registration.emergencyContact?.phone].filter(Boolean).join(" · ") || "Not recorded"}
                </small>
                <small>
                  Health: {[registration.student?.allergies, registration.student?.healthNotes].filter(Boolean).join(" · ") || "None recorded"}
                </small>
              </div>
              <div className="attendance-status-buttons" aria-label={`Attendance for ${registration.student?.name || "student"}`}>
                {(["present", "absent", "leave"] as AttendanceStatus[]).map((status) => (
                  <button
                    type="button"
                    key={status}
                    className={currentStatus === status ? `active ${status}` : ""}
                    onClick={() => updateStatus(registration.id, status)}
                  >
                    {attendanceStatusLabel(status)}
                  </button>
                ))}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function AdminRegistrationsPanel({
  registrations,
  classes,
  holidayPrograms,
  schoolSlug,
  staffRole,
  setError,
  setNotice,
  onReload,
  canOpenStudentProfile,
  onOpenStudentProfile,
  onOpenRegistrationEdit
}: {
  registrations: Registration[];
  classes: ClassItem[];
  holidayPrograms: HolidayProgram[];
  schoolSlug: string;
  staffRole?: SessionProfile["role"];
  setError: (message: string) => void;
  setNotice: (message: string) => void;
  onReload: () => Promise<void>;
  canOpenStudentProfile?: boolean;
  onOpenStudentProfile?: (studentId?: string) => void;
  onOpenRegistrationEdit?: (registration: Registration) => void;
}) {
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [workingId, setWorkingId] = useState("");
  const [selectedRegistrationId, setSelectedRegistrationId] = useState("");
  const [isSendingReminders, setIsSendingReminders] = useState(false);
  const classById = useMemo(() => new Map(classes.map((klass) => [klass.id, klass])), [classes]);
  const programById = useMemo(() => new Map(holidayPrograms.map((program) => [program.id, program])), [holidayPrograms]);
  const canManagePayments = staffRole === "admin" || staffRole === "finance";
  const showFinancials = staffRole !== "coach";

  const filteredRegistrations = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    return registrations.filter((registration) => {
      const type = registration.programType === "holiday" ? "holiday" : "class";
      const matchesPayment = !showFinancials || paymentFilter === "all" || registration.paymentStatus === paymentFilter;
      const matchesType = typeFilter === "all" || type === typeFilter;
      const klass = registration.classId ? classById.get(registration.classId) : undefined;
      const program = registration.programId ? programById.get(registration.programId) : undefined;
      const haystack = [
        registration.student?.name,
        registration.guardianPhone,
        registration.guardianEmail,
        registration.parent?.name,
        registration.parent?.email,
        klass?.name,
        program?.name
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return matchesPayment && matchesType && (!searchText || haystack.includes(searchText));
    });
  }, [classById, paymentFilter, programById, registrations, search, showFinancials, typeFilter]);

  const paidCount = registrations.filter((registration) => registration.paymentStatus === "paid").length;
  const unpaidRegistrations = filteredRegistrations.filter((registration) => registration.paymentStatus !== "paid");
  const outstandingCents = registrations
    .filter((registration) => registration.paymentStatus !== "paid")
    .reduce((sum, registration) => sum + Number(registration.priceCents ?? 0), 0);

  async function updatePayment(registration: Registration, paymentStatus: "paid" | "unpaid") {
    setWorkingId(registration.id);
    setError("");
    try {
      await api(`/admin/schools/${schoolSlug}/registrations/${encodeURIComponent(registration.id)}/payment`, {
        method: "PUT",
        body: JSON.stringify({
          paymentStatus,
          paymentMethod: paymentStatus === "paid" ? registration.paymentMethod || "Manual" : "",
          amountPaidCents: paymentStatus === "paid" ? registration.priceCents : 0,
          paymentNote: paymentStatus === "paid" ? "Marked paid from admin registrations" : "Marked unpaid from admin registrations"
        })
      });
      setNotice(`Registration marked ${paymentStatus}.`);
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update payment");
    } finally {
      setWorkingId("");
    }
  }

  async function sendUnpaidReminders() {
    setIsSendingReminders(true);
    setError("");
    try {
      const result = await api<{ sentCount: number; registrationCount: number }>(`/admin/schools/${schoolSlug}/registrations/unpaid-reminders`, {
        method: "POST",
        body: JSON.stringify({
          paymentBaseUrl: `${window.location.origin}/${schoolSlug}`,
          registrationIds: unpaidRegistrations.map((registration) => registration.id)
        })
      });
      setNotice(`Sent ${result.sentCount} reminder email${result.sentCount === 1 ? "" : "s"} for ${result.registrationCount} shown unpaid registration${result.registrationCount === 1 ? "" : "s"}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to send unpaid reminders");
    } finally {
      setIsSendingReminders(false);
    }
  }

  return (
    <section className="panel admin-registrations-panel">
      <div className="admin-list-header">
        <div>
          <h2>{staffRole === "coach" ? "Coach roster" : "Registrations & Payments"}</h2>
          <p className="muted">{filteredRegistrations.length} registration{filteredRegistrations.length === 1 ? "" : "s"} shown</p>
        </div>
        {canManagePayments ? (
          <button className="primary" type="button" onClick={sendUnpaidReminders} disabled={unpaidRegistrations.length === 0 || isSendingReminders}>
            <Mail size={16} />
            {isSendingReminders
              ? "Sending..."
              : `Send to ${unpaidRegistrations.length} shown unpaid registration${unpaidRegistrations.length === 1 ? "" : "s"}`}
          </button>
        ) : null}
      </div>
      {showFinancials ? (
        <div className="admin-metric-grid">
          <div className="admin-metric">
            <span>Total</span>
            <strong>{registrations.length}</strong>
          </div>
          <div className="admin-metric">
            <span>Paid</span>
            <strong>{paidCount}</strong>
          </div>
          <div className="admin-metric">
            <span>Unpaid</span>
            <strong>{registrations.length - paidCount}</strong>
          </div>
          <div className="admin-metric">
            <span>Outstanding</span>
            <strong>{formatAud(outstandingCents)}</strong>
          </div>
        </div>
      ) : null}
      <div className="admin-filter-bar">
        <div>
          <label>Search</label>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Student, parent, phone, email" />
        </div>
        {showFinancials ? (
          <div>
            <label>Payment</label>
            <select value={paymentFilter} onChange={(event) => setPaymentFilter(event.target.value)}>
              <option value="all">All payments</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="refunded">Refunded</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        ) : null}
        <div>
          <label>Type</label>
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
            <option value="all">All types</option>
            <option value="class">Term classes</option>
            <option value="holiday">Holiday programs</option>
          </select>
        </div>
      </div>
      <div className="registration-list">
        {filteredRegistrations.length === 0 ? <p className="muted">No registrations match the current filters.</p> : null}
        {filteredRegistrations.length > 0 ? (
          <div className={`registration-row ${showFinancials ? "" : "roster-row"} registration-row-heading`} aria-hidden="true">
            <span>Student</span>
            <span>Type</span>
            <span>Program/Class</span>
            <span>Schedule/Blocks</span>
            {showFinancials ? (
              <>
                <span>Amount</span>
                <span>Payment</span>
                <span>Action</span>
              </>
            ) : null}
          </div>
        ) : null}
        {filteredRegistrations.map((registration) => (
          <AdminRegistrationCard
            key={registration.id}
            registration={registration}
            klass={registration.classId ? classById.get(registration.classId) : undefined}
            program={registration.programId ? programById.get(registration.programId) : undefined}
            canManagePayments={canManagePayments}
            showFinancials={showFinancials}
            showHealth={staffRole !== "finance"}
            isSelected={selectedRegistrationId === registration.id}
            isWorking={workingId === registration.id}
            onSelectStudent={() => setSelectedRegistrationId((current) => (current === registration.id ? "" : registration.id))}
            canOpenStudentProfile={Boolean(canOpenStudentProfile)}
            onOpenStudentProfile={onOpenStudentProfile}
            onOpenRegistrationEdit={onOpenRegistrationEdit}
            onUpdatePayment={updatePayment}
          />
        ))}
      </div>
    </section>
  );
}

function AdminRegistrationCard({
  registration,
  klass,
  program,
  canManagePayments,
  showFinancials,
  showHealth,
  isSelected,
  isWorking,
  onSelectStudent,
  canOpenStudentProfile,
  onOpenStudentProfile,
  onOpenRegistrationEdit,
  onUpdatePayment
}: {
  registration: Registration;
  klass?: ClassItem;
  program?: HolidayProgram;
  canManagePayments: boolean;
  showFinancials: boolean;
  showHealth: boolean;
  isSelected: boolean;
  isWorking: boolean;
  onSelectStudent: () => void;
  canOpenStudentProfile: boolean;
  onOpenStudentProfile?: (studentId?: string) => void;
  onOpenRegistrationEdit?: (registration: Registration) => void;
  onUpdatePayment: (registration: Registration, paymentStatus: "paid" | "unpaid") => Promise<void>;
}) {
  const isHoliday = registration.programType === "holiday";
  const paymentClass = registration.paymentStatus === "paid" ? "paid" : "unpaid";
  const programName = program?.name ?? klass?.name ?? "Program not loaded";
  const scheduleSummary = isHoliday ? holidayRegistrationSummary(registration) : klass?.schedule || "Schedule not recorded";
  return (
    <article className={`registration-row-card ${isSelected ? "selected" : ""}`}>
      <div className={`registration-row ${showFinancials ? "" : "roster-row"}`}>
        <button className="student-link-button" type="button" onClick={onSelectStudent} aria-expanded={isSelected}>
          {registration.student?.name || "Unnamed student"}
        </button>
        <span>{isHoliday ? "Holiday" : "Class"}</span>
        <strong title={programName}>{programName}</strong>
        <span title={scheduleSummary}>{scheduleSummary}</span>
        {showFinancials ? (
          <>
            <strong className="amount-cell">{formatAud(Number(registration.priceCents ?? 0))}</strong>
            <span className={`status-pill ${paymentClass}`}>{registration.paymentStatus}</span>
            {canManagePayments ? (
              registration.paymentStatus === "paid" ? (
                <button type="button" onClick={() => onUpdatePayment(registration, "unpaid")} disabled={isWorking}>
                  {isWorking ? "Saving..." : "Mark unpaid"}
                </button>
              ) : (
                <button className="primary" type="button" onClick={() => onUpdatePayment(registration, "paid")} disabled={isWorking}>
                  <CreditCard size={16} />
                  {isWorking ? "Saving..." : "Mark paid"}
                </button>
              )
            ) : (
              <span className="muted">View only</span>
            )}
          </>
        ) : null}
      </div>
      {isSelected ? (
        <div className="registration-student-detail">
          <div className="registration-detail-grid">
            <div>
              <span>Student</span>
              <strong>{studentAgeSentence(registration.student?.dateOfBirth)}</strong>
              <small>DOB {registration.student?.dateOfBirth || "Not recorded"}</small>
            </div>
            <div>
              <span>Parent</span>
              <strong>{registration.parent?.name || registration.guardianPhone || "Not recorded"}</strong>
              <small>{registration.guardianEmail || registration.parent?.email || "No email"}</small>
            </div>
            <div>
              <span>Registration</span>
              <strong>{registration.status}</strong>
              <small>{registration.createdAt ? `Created ${registration.createdAt.slice(0, 10)}` : "Created date not recorded"}</small>
            </div>
            {showFinancials ? (
              <div>
                <span>Payment</span>
                <strong>{registration.paymentMethod || "No payment method"}</strong>
                <small>{registration.paidAt ? `Paid ${registration.paidAt.slice(0, 10)}` : "Paid date not recorded"}</small>
              </div>
            ) : null}
          </div>
          <div className="registration-program-detail">
            {isHoliday ? (
              <>
                <strong>{registration.selectedBlocks?.length ?? 0} holiday block{registration.selectedBlocks?.length === 1 ? "" : "s"}</strong>
                <span>{registration.selectedBlocks?.map((block) => `${block.date} ${block.startTime}-${block.endTime}`).join(", ") || "No blocks recorded"}</span>
              </>
            ) : (
              <>
                <strong>{klass?.schedule || "Schedule not recorded"}</strong>
                <span>{klass?.venueName || klass?.venueAddress || registration.venueId || "Venue not recorded"}</span>
              </>
            )}
          </div>
          {showHealth ? (
            <div className="registration-health">
              <span>Allergies: {registration.student?.allergies || "None recorded"}</span>
              <span>Health: {registration.student?.healthNotes || "None recorded"}</span>
              <span>Emergency: {registration.emergencyContact?.name || "Not recorded"} {registration.emergencyContact?.phone || ""}</span>
            </div>
          ) : null}
          {canOpenStudentProfile && registration.studentId ? (
            <div className="inline form-actions registration-profile-actions">
              <button className="primary" type="button" onClick={() => onOpenStudentProfile?.(registration.studentId)}>
                <UserCircle size={16} />
                Open student profile
              </button>
              <button type="button" onClick={() => onOpenRegistrationEdit?.(registration)}>
                Edit registration
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function holidayRegistrationSummary(registration: Registration) {
  const blocks = registration.selectedBlocks ?? [];
  if (blocks.length === 0) return "No blocks";
  const dates = blocks.map((block) => block.date).filter(Boolean).sort();
  const firstDate = dates[0] ?? "";
  const lastDate = dates[dates.length - 1] ?? "";
  const dateText = firstDate && lastDate && firstDate !== lastDate ? `${firstDate} to ${lastDate}` : firstDate || "Dates not recorded";
  return `${blocks.length} block${blocks.length === 1 ? "" : "s"}, ${dateText}`;
}

function buildCoachSessions(
  registrations: Registration[],
  classById: Map<string, ClassItem>,
  termById: Map<string, Term>,
  programById: Map<string, HolidayProgram>,
  range: { start: string; end: string }
) {
  const sessions = new Map<string, CoachSession>();

  registrations.forEach((registration) => {
    if (registration.programType === "holiday") {
      const program = registration.programId ? programById.get(registration.programId) : undefined;
      (registration.selectedBlocks ?? []).forEach((block) => {
        if (!dateInRange(block.date, range.start, range.end)) return;
        const role = registration.coachRole === "substitute" ? "substitute" : "primary";
        const sessionId = `holiday-${registration.programId}-${block.id}-${role}`;
        upsertCoachSession(sessions, sessionId, {
          type: "holiday",
          programId: registration.programId,
          blockId: block.id,
          date: block.date,
          startTime: block.startTime,
          endTime: block.endTime,
          title: program?.name ?? "Holiday program",
          venue: program?.venueName || program?.venueAddress || registration.venueId || "",
          role,
          registration
        });
      });
      return;
    }

    const klass = registration.classId ? classById.get(registration.classId) : undefined;
    if (!klass) return;
    const times = parseClassScheduleTimes(klass.schedule);
    const role = registration.coachRole === "substitute" ? "substitute" : "primary";
    const dates = registration.coachSessionDate
      ? [registration.coachSessionDate].filter((date) => dateInRange(date, range.start, range.end))
      : classSessionDates(klass, termById.get(klass.termId), range);
    dates.forEach((date) => {
      const sessionId = `class-${klass.id}-${date}-${role}`;
      upsertCoachSession(sessions, sessionId, {
        type: "class",
        classId: klass.id,
        date,
        startTime: times.startTime,
        endTime: times.endTime,
        title: klass.name,
        venue: klass.venueName || klass.venueAddress || registration.venueId || "",
        role,
        registration
      });
    });
  });

  return [...sessions.values()].sort((a, b) => `${a.date} ${a.startTime} ${a.title}`.localeCompare(`${b.date} ${b.startTime} ${b.title}`));
}

function upsertCoachSession(
  sessions: Map<string, CoachSession>,
  id: string,
  data: Omit<CoachSession, "id" | "registrations"> & { registration: Registration }
) {
  const existing = sessions.get(id);
  if (existing) {
    existing.registrations.push(data.registration);
    return;
  }
  sessions.set(id, {
    id,
    type: data.type,
    classId: data.classId,
    programId: data.programId,
    blockId: data.blockId,
    date: data.date,
    startTime: data.startTime,
    endTime: data.endTime,
    title: data.title,
    venue: data.venue,
    role: data.role,
    registrations: [data.registration]
  });
}

function classSessionDates(klass: ClassItem, term: Term | undefined, range: { start: string; end: string }) {
  if (!klass.startsAt) return [];
  const end = minDate(term?.endsAt || range.end, range.end);
  if (end < range.start) return [];
  const dates: string[] = [];
  let cursor = klass.startsAt;
  while (cursor < range.start) cursor = addDays(cursor, 7);
  while (cursor <= end) {
    dates.push(cursor);
    cursor = addDays(cursor, 7);
  }
  return dates;
}

function coachScheduleDateRange(range: CoachScheduleRange) {
  const today = formatInputDate(new Date());
  if (range === "today") return { start: today, end: today };
  if (range === "week") {
    const todayParts = parseDateParts(today);
    if (!todayParts) return { start: today, end: today };
    const date = new Date(todayParts.year, todayParts.month - 1, todayParts.day);
    const mondayOffset = date.getDay() === 0 ? -6 : 1 - date.getDay();
    const monday = formatInputDate(addDaysToDate(date, mondayOffset));
    return { start: monday, end: formatInputDate(addDaysToDate(date, mondayOffset + 6)) };
  }
  return { start: today, end: addDays(today, 84) };
}

function dateInRange(date: string, start: string, end: string) {
  return Boolean(date) && date >= start && date <= end;
}

function addDays(date: string, days: number) {
  const parts = parseDateParts(date);
  if (!parts) return date;
  return formatInputDate(addDaysToDate(new Date(parts.year, parts.month - 1, parts.day), days));
}

function addDaysToDate(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function minDate(a: string, b: string) {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
}

function formatCoachSessionDay(date: string) {
  const parts = parseDateParts(date);
  if (!parts) return date || "Date not recorded";
  return new Date(parts.year, parts.month - 1, parts.day).toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short"
  });
}

function formatCoachSessionTime(startTime: string, endTime: string) {
  if (!startTime && !endTime) return "Time not recorded";
  return `${startTime ? formatScheduleTime(startTime) : "?"} - ${endTime ? formatScheduleTime(endTime) : "?"}`;
}

function uniqueRegistrations(registrations: Registration[]) {
  const seen = new Set<string>();
  return registrations.filter((registration) => {
    if (seen.has(registration.id)) return false;
    seen.add(registration.id);
    return true;
  });
}

function uniqueRegistrationCount(registrations: Registration[]) {
  return uniqueRegistrations(registrations).length;
}

function attendanceQueryString(session: CoachSession) {
  const params = new URLSearchParams({
    sessionType: session.type,
    date: session.date
  });
  if (session.classId) params.set("classId", session.classId);
  if (session.programId) params.set("programId", session.programId);
  if (session.blockId) params.set("blockId", session.blockId);
  return params.toString();
}

function attendanceSummary(registrations: Registration[], records: Record<string, AttendanceStatus>) {
  return registrations.reduce(
    (summary, registration) => {
      const status = records[registration.id] ?? "not_marked";
      summary[status] += 1;
      return summary;
    },
    { present: 0, absent: 0, leave: 0, not_marked: 0 }
  );
}

function attendanceStatusLabel(status: AttendanceStatus) {
  const labels: Record<AttendanceStatus, string> = {
    not_marked: "Not marked",
    present: "Present",
    absent: "Absent",
    leave: "Leave"
  };
  return labels[status];
}

function AdminFields({
  collection,
  item,
  venues,
  terms,
  classTemplates,
  members,
  classes,
  holidayPrograms
}: {
  collection: string;
  item?: Record<string, unknown> | null;
  venues: Record<string, unknown>[];
  terms: Record<string, unknown>[];
  classTemplates: ClassTemplate[];
  members: Record<string, unknown>[];
  classes: ClassItem[];
  holidayPrograms: HolidayProgram[];
}) {
  const coachMembers = members.filter((member) => member.role === "coach") as unknown as StaffMember[];
  if (collection === "venues") {
    return (
      <>
        <label>Name</label>
        <input name="name" defaultValue={adminFieldValue(item, "name")} required />
        <label>Address</label>
        <input name="address" defaultValue={adminFieldValue(item, "address")} required />
      </>
    );
  }
  if (collection === "terms") {
    return (
      <>
        <label>Name</label>
        <input name="name" placeholder="Term 3 2026" defaultValue={adminFieldValue(item, "name")} required />
        <label>Starts at</label>
        <input name="startsAt" type="date" defaultValue={adminFieldValue(item, "startsAt")} required />
        <label>Ends at</label>
        <input name="endsAt" type="date" defaultValue={adminFieldValue(item, "endsAt")} required />
      </>
    );
  }
  if (collection === "members") {
    return (
      <>
        <label>Name</label>
        <input name="name" defaultValue={adminFieldValue(item, "name")} />
        <label>Email</label>
        <input name="email" type="email" placeholder="coach@example.com" defaultValue={adminFieldValue(item, "email")} />
        <label>Phone</label>
        <input name="phone" type="tel" inputMode="tel" placeholder="+61412345678" defaultValue={adminFieldValue(item, "phone")} />
        <label>Role</label>
        <select name="role" defaultValue={adminFieldValue(item, "role") || "coach"}>
          <option value="coach">coach</option>
          <option value="finance">finance</option>
          <option value="admin">admin</option>
        </select>
      </>
    );
  }
  if (collection === "class-templates") {
    const currentTemplateId = adminItemKey(item ?? {});
    return (
      <>
        <label>Template name</label>
        <input name="name" placeholder="Little Warriors - Beginner Kids" defaultValue={adminFieldValue(item, "name")} required />
        <div className="two-cols">
          <div>
            <label>Level</label>
            <select name="level" defaultValue={adminFieldValue(item, "level") || "Beginner"}>
              {courseLevels.map((courseLevel) => (
                <option value={courseLevel} key={courseLevel}>
                  {courseLevel}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Age group</label>
            <input name="ageGroup" placeholder="Kids 8-12" defaultValue={adminFieldValue(item, "ageGroup")} />
          </div>
        </div>
        <label>Description</label>
        <textarea name="description" rows={2} defaultValue={adminFieldValue(item, "description")} />
        <div className="two-cols">
          <div>
            <label>Default capacity</label>
            <input name="defaultCapacity" type="number" min="1" defaultValue={adminFieldValue(item, "defaultCapacity")} />
          </div>
          <div>
            <label>Default price AUD</label>
            <input name="defaultPriceAud" type="number" min="0" step="0.01" defaultValue={templatePriceAud(item, "defaultPriceCents")} />
          </div>
        </div>
        <div className="two-cols">
          <div>
            <label>Default duration minutes</label>
            <input name="defaultDurationMinutes" type="number" min="15" step="15" defaultValue={adminFieldValue(item, "defaultDurationMinutes") || "60"} />
          </div>
          <div>
            <label>Display order</label>
            <input name="sortOrder" type="number" defaultValue={adminFieldValue(item, "sortOrder") || "0"} />
            <p className="muted">Lower numbers appear first in template dropdowns and lists.</p>
          </div>
        </div>
        <label>Default venue</label>
        <select name="defaultVenueId" defaultValue={adminFieldValue(item, "defaultVenueId")}>
          <option value="">No default venue</option>
          {venues.map((venue) => (
            <option value={adminItemKey(venue)} key={adminItemKey(venue)}>
              {adminOptionLabel(venue)}
            </option>
          ))}
        </select>
        <CoachSelect
          name="defaultCoachPhone"
          label="Default coach"
          coaches={coachMembers}
          defaultValue={adminFieldValue(item, "defaultCoachPhone")}
          allowEmpty
        />
        <label>Next template</label>
        <select name="nextTemplateId" defaultValue={adminFieldValue(item, "nextTemplateId")}>
          <option value="">No automatic progression</option>
          {classTemplates
            .filter((template) => template.id !== currentTemplateId)
            .map((template) => (
              <option value={template.id} key={template.id}>
                {template.name}
              </option>
            ))}
        </select>
        <label className="open-toggle">
          <input name="active" type="checkbox" value="true" defaultChecked={item?.active === undefined ? true : Boolean(item.active)} />
          Active
        </label>
      </>
    );
  }
  if (collection === "holiday-programs") {
    return (
      <>
        <label>Name</label>
        <input name="name" defaultValue={adminFieldValue(item, "name")} required />
        <label>Venue</label>
        <select name="venueId" defaultValue={adminFieldValue(item, "venueId")} required>
          <option value="" disabled>
            Select venue
          </option>
          {venues.map((venue) => (
            <option value={adminItemKey(venue)} key={adminItemKey(venue)}>
              {adminOptionLabel(venue)}
            </option>
          ))}
        </select>
        <CoachSelect
          name="coachPhone"
          label="Primary coach"
          coaches={coachMembers}
          defaultValue={adminFieldValue(item, "coachPhone")}
          allowEmpty
        />
        <div className="two-cols">
          <div>
            <label>Default block price AUD</label>
            <input name="blockPriceAud" type="number" min="0" step="0.01" defaultValue={holidayBlockPriceAud(item)} required />
          </div>
          <div>
            <label>Level</label>
            <select name="level" defaultValue={adminFieldValue(item, "level") || "Beginner"}>
              {courseLevels.map((courseLevel) => (
                <option value={courseLevel} key={courseLevel}>
                  {courseLevel}
                </option>
              ))}
            </select>
          </div>
        </div>
        <label>Age group</label>
        <input name="ageGroup" placeholder="Kids 8-12" defaultValue={adminFieldValue(item, "ageGroup")} />
        <label>Description</label>
        <textarea name="description" rows={2} defaultValue={adminFieldValue(item, "description")} />
        <HolidayBlocksBuilder item={item} />
      </>
    );
  }
  if (collection === "notifications") {
    return (
      <>
        <label>Class</label>
        <select name="classId" defaultValue={adminFieldValue(item, "classId")} required>
          <option value="" disabled>
            Select class
          </option>
          {classes.map((klass) => (
            <option value={klass.id} key={klass.id}>
              {klass.name}{klass.schedule ? ` · ${klass.schedule}` : ""}
            </option>
          ))}
        </select>
        <label>Subject</label>
        <input name="subject" defaultValue={adminFieldValue(item, "subject")} required />
        <label>Message</label>
        <textarea name="message" rows={5} defaultValue={adminFieldValue(item, "message")} required />
      </>
    );
  }
  if (collection === "coach-coverages") {
    return <CoachCoverageFields item={item} coaches={coachMembers} classes={classes} holidayPrograms={holidayPrograms} />;
  }
  return <ClassFields item={item} venues={venues} terms={terms} coaches={coachMembers} classTemplates={classTemplates} />;
}

function ClassFields({
  item,
  venues,
  terms,
  coaches,
  classTemplates
}: {
  item?: Record<string, unknown> | null;
  venues: Record<string, unknown>[];
  terms: Record<string, unknown>[];
  coaches: StaffMember[];
  classTemplates: ClassTemplate[];
}) {
  const [templateId, setTemplateId] = useState(adminFieldValue(item, "templateId"));
  const template = classTemplates.find((candidate) => candidate.id === templateId);
  const duration = Number(template?.defaultDurationMinutes ?? 60);
  const defaultStart = classScheduleStartTime(item);
  const defaultEnd = classScheduleEndTime(item) || addMinutesToTime(defaultStart, duration);
  return (
    <>
      <label>Class template</label>
      <select name="templateId" value={templateId} onChange={(event) => setTemplateId(event.target.value)}>
        <option value="">No template</option>
        {classTemplates
          .filter((candidate) => candidate.active !== false)
          .sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0) || a.name.localeCompare(b.name))
          .map((candidate) => (
            <option value={candidate.id} key={candidate.id}>
              {candidate.name}
            </option>
          ))}
      </select>
      <div key={templateId || "manual"}>
        <label>Name</label>
        <input name="name" defaultValue={adminFieldValue(item, "name") || template?.name || ""} required />
        <label>Term</label>
        <select name="termId" defaultValue={adminFieldValue(item, "termId")} required>
          <option value="" disabled>
            Select term
          </option>
          {terms.map((term) => (
            <option value={adminItemKey(term)} key={adminItemKey(term)}>
              {adminOptionLabel(term)}
            </option>
          ))}
        </select>
        <label>Venue</label>
        <select name="venueId" defaultValue={adminFieldValue(item, "venueId") || template?.defaultVenueId || ""} required>
          <option value="" disabled>
            Select venue
          </option>
          {venues.map((venue) => (
            <option value={adminItemKey(venue)} key={adminItemKey(venue)}>
              {adminOptionLabel(venue)}
            </option>
          ))}
        </select>
        <CoachSelect
          name="coachPhone"
          label="Primary coach"
          coaches={coaches}
          defaultValue={adminFieldValue(item, "coachPhone") || template?.defaultCoachPhone || ""}
          allowEmpty
        />
        <label>Weekly class starts on</label>
        <input name="startsAt" type="date" defaultValue={adminFieldValue(item, "startsAt")} required />
        <div className="two-cols">
          <div>
            <label>Start time</label>
            <input name="startTime" type="time" defaultValue={defaultStart} required />
          </div>
          <div>
            <label>End time</label>
            <input name="endTime" type="time" defaultValue={defaultEnd} required />
          </div>
        </div>
        <div className="two-cols">
          <div>
            <label>Capacity</label>
            <input name="capacity" type="number" min="1" defaultValue={adminFieldValue(item, "capacity") || String(template?.defaultCapacity ?? "")} required />
          </div>
          <div>
            <label>Price AUD</label>
            <input name="priceAud" type="number" min="0" step="0.01" defaultValue={classPriceAud(item) || centsToAudInput(template?.defaultPriceCents)} required />
          </div>
        </div>
        <label>Level</label>
        <select name="level" defaultValue={adminFieldValue(item, "level") || template?.level || "Beginner"}>
          {courseLevels.map((courseLevel) => (
            <option value={courseLevel} key={courseLevel}>
              {courseLevel}
            </option>
          ))}
        </select>
        <label>Age group</label>
        <input name="ageGroup" placeholder="Kids 8-12" defaultValue={adminFieldValue(item, "ageGroup") || template?.ageGroup || ""} />
      </div>
    </>
  );
}

function CoachSelect({
  name,
  label,
  coaches,
  defaultValue,
  allowEmpty = false
}: {
  name: string;
  label: string;
  coaches: StaffMember[];
  defaultValue?: string;
  allowEmpty?: boolean;
}) {
  return (
    <>
      <label>{label}</label>
      <select name={name} defaultValue={defaultValue ?? ""} required={!allowEmpty}>
        {allowEmpty ? <option value="">No coach assigned</option> : null}
        {!allowEmpty ? (
          <option value="" disabled>
            Select coach
          </option>
        ) : null}
        {coaches.map((coach) => (
          <option value={staffMemberKey(coach)} key={staffMemberKey(coach)}>
            {coachLabel(coach)}
          </option>
        ))}
      </select>
      {coaches.length === 0 ? <p className="muted">Add coach members under Staff before assigning coaches.</p> : null}
    </>
  );
}

function CoachCoverageFields({
  item,
  coaches,
  classes,
  holidayPrograms
}: {
  item?: Record<string, unknown> | null;
  coaches: StaffMember[];
  classes: ClassItem[];
  holidayPrograms: HolidayProgram[];
}) {
  const [targetType, setTargetType] = useState<"class" | "holiday">(adminFieldValue(item, "targetType") === "holiday" ? "holiday" : "class");
  const [programId, setProgramId] = useState(adminFieldValue(item, "programId") || holidayPrograms[0]?.id || "");
  const selectedProgram = holidayPrograms.find((program) => program.id === programId);

  return (
    <>
      <label>Coverage type</label>
      <select name="targetType" value={targetType} onChange={(event) => setTargetType(event.target.value as "class" | "holiday")}>
        <option value="class">Term class session</option>
        <option value="holiday">Holiday program block</option>
      </select>
      <CoachSelect name="coachPhone" label="Substitute coach" coaches={coaches} defaultValue={adminFieldValue(item, "coachPhone")} />
      {targetType === "class" ? (
        <>
          <label>Class</label>
          <select name="classId" defaultValue={adminFieldValue(item, "classId")} required>
            <option value="" disabled>
              Select class
            </option>
            {classes.map((klass) => (
              <option value={klass.id} key={klass.id}>
                {klass.name}
              </option>
            ))}
          </select>
          <label>Session date</label>
          <input name="date" type="date" defaultValue={adminFieldValue(item, "date")} required />
        </>
      ) : (
        <>
          <label>Holiday program</label>
          <select name="programId" value={programId} onChange={(event) => setProgramId(event.target.value)} required>
            <option value="" disabled>
              Select holiday program
            </option>
            {holidayPrograms.map((program) => (
              <option value={program.id} key={program.id}>
                {program.name}
              </option>
            ))}
          </select>
          <label>Block</label>
          <select name="blockId" defaultValue={adminFieldValue(item, "blockId")} required>
            <option value="" disabled>
              Select block
            </option>
            {(selectedProgram?.blocks ?? []).map((block) => (
              <option value={block.id} key={block.id}>
                {holidayBlockOptionLabel(block)}
              </option>
            ))}
          </select>
          <input name="date" type="hidden" value="" readOnly />
        </>
      )}
      <label>Note</label>
      <textarea name="note" rows={2} placeholder="Tony away, Amy covering this session" defaultValue={adminFieldValue(item, "note")} />
    </>
  );
}

function coachLabel(coach: StaffMember) {
  return [coach.name || "Coach", coach.email || coach.phone].filter(Boolean).join(" - ");
}

function staffMemberKey(member: StaffMember) {
  return member.userKey || member.phone || (member.email ? `EMAIL#${member.email.toLowerCase()}` : "");
}

function sessionStaffKey(sessionProfile: SessionProfile | null) {
  if (!sessionProfile) return "";
  return comparableStaffKey(sessionProfile.userKey || sessionProfile.staff?.userKey || sessionProfile.phone || sessionProfile.email || sessionProfile.staff?.email || "");
}

function recordStaffKey(item: Record<string, unknown>) {
  return comparableStaffKey(String(item.userKey || item.phone || item.email || ""));
}

function comparableStaffKey(value: string) {
  const raw = value.trim();
  if (!raw) return "";
  if (raw.startsWith("EMAIL#")) return `EMAIL#${raw.slice("EMAIL#".length).toLowerCase()}`;
  if (raw.includes("@")) return `EMAIL#${raw.toLowerCase()}`;
  return raw.replace(/[\s()-]/g, "");
}

function staffDeleteBlockReason(
  item: Record<string, unknown>,
  currentStaffKey: string,
  staffItems: Record<string, unknown>[],
  classes: ClassItem[],
  holidayPrograms: HolidayProgram[],
  coverages: Record<string, unknown>[]
) {
  const staffKey = recordStaffKey(item);
  if (staffKey && currentStaffKey && staffKey === currentStaffKey) return "You cannot delete your own account";
  if (item.role === "admin" && staffItems.filter((staff) => staff.role === "admin").length <= 1) return "Cannot delete the last admin";
  if (item.role === "coach") {
    if (classes.some((klass) => comparableStaffKey(String(klass.coachPhone ?? "")) === staffKey)) return "Coach is assigned to active classes";
    if (holidayPrograms.some((program) => comparableStaffKey(String(program.coachPhone ?? "")) === staffKey)) return "Coach is assigned to holiday programs";
    if (coverages.some((coverage) => comparableStaffKey(String(coverage.coachPhone ?? "")) === staffKey)) return "Coach is assigned to coverage";
  }
  return "";
}

function holidayBlockOptionLabel(block: HolidayProgramBlock) {
  return `${block.date} ${block.startTime}-${block.endTime}`;
}

interface AdminHolidayBlock {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  capacity: number;
  priceCents?: number;
  open: boolean;
}

interface HolidayTimeSlot {
  startTime: string;
  endTime: string;
}

function HolidayBlocksBuilder({ item }: { item?: Record<string, unknown> | null }) {
  const initialBlocks = holidayBlocksFromItem(item);
  const [blocks, setBlocks] = useState<AdminHolidayBlock[]>(initialBlocks);
  const [rangeStart, setRangeStart] = useState(adminFieldValue(item, "startsAt") || initialBlocks[0]?.date || "");
  const [rangeEnd, setRangeEnd] = useState(adminFieldValue(item, "endsAt") || initialBlocks[initialBlocks.length - 1]?.date || "");
  const [selectedWeekdays, setSelectedWeekdays] = useState<string[]>(weekdays.slice(0, 5));
  const [timeSlots, setTimeSlots] = useState<HolidayTimeSlot[]>(holidayTimeSlotsFromBlocks(initialBlocks));
  const [capacity, setCapacity] = useState(initialBlocks[0]?.capacity ? String(initialBlocks[0].capacity) : "");
  const [priceAud, setPriceAud] = useState("");

  function toggleWeekday(weekday: string) {
    setSelectedWeekdays((current) => (current.includes(weekday) ? current.filter((value) => value !== weekday) : [...current, weekday]));
  }

  function updateTimeSlot(index: number, key: keyof HolidayTimeSlot, value: string) {
    setTimeSlots((current) => current.map((slot, slotIndex) => (slotIndex === index ? { ...slot, [key]: value } : slot)));
  }

  function addTimeSlot() {
    setTimeSlots((current) => [...current, { startTime: "", endTime: "" }]);
  }

  function removeTimeSlot(index: number) {
    setTimeSlots((current) => current.filter((_, slotIndex) => slotIndex !== index));
  }

  function generateBlocks() {
    const dates = holidayDatesInRange(rangeStart, rangeEnd).filter((date) => selectedWeekdays.includes(weekdayForDate(date)));
    const slots = timeSlots.filter((slot) => slot.startTime && slot.endTime);
    const generatedBlocks = dates.flatMap((date) =>
      slots.map((slot, index) => ({
        id: holidayBlockId(date, slot.startTime, slot.endTime, index),
        date,
        startTime: slot.startTime,
        endTime: slot.endTime,
        capacity: Number(capacity || 0),
        ...(priceAud ? { priceCents: Math.round(Number(priceAud) * 100) } : {}),
        open: true
      }))
    );
    setBlocks(generatedBlocks);
  }

  function addBlock() {
    setBlocks((current) => [
      ...current,
      {
        id: holidayBlockId(rangeStart || formatInputDate(new Date()), "", "", current.length),
        date: rangeStart || "",
        startTime: "",
        endTime: "",
        capacity: Number(capacity || 0),
        open: true
      }
    ]);
  }

  function updateBlock(index: number, key: keyof AdminHolidayBlock, value: string | boolean) {
    setBlocks((current) =>
      current.map((block, blockIndex) => {
        if (blockIndex !== index) return block;
        if (key === "capacity") return { ...block, capacity: Number(value || 0) };
        if (key === "priceCents") return value ? { ...block, priceCents: Math.round(Number(value) * 100) } : withoutPriceCents(block);
        if (key === "open") return { ...block, open: Boolean(value) };
        return { ...block, [key]: String(value) };
      })
    );
  }

  function removeBlock(index: number) {
    setBlocks((current) => current.filter((_, blockIndex) => blockIndex !== index));
  }

  return (
    <section className="holiday-block-builder" aria-label="Holiday program blocks">
      <input name="blocksJson" type="hidden" value={JSON.stringify(blocks)} />
      <div className="holiday-builder-section">
        <div>
          <h3>Blocks</h3>
          <p className="muted">Generate blocks from a date range and time slots, then adjust individual blocks below.</p>
        </div>
        <div className="two-cols">
          <div>
            <label>Generate from</label>
            <input type="date" value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} />
          </div>
          <div>
            <label>Generate to</label>
            <input type="date" value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} />
          </div>
        </div>
        <div>
          <label>Weekdays</label>
          <div className="weekday-options">
            {weekdays.map((weekday) => (
              <label className="checkbox-pill" key={weekday}>
                <input type="checkbox" checked={selectedWeekdays.includes(weekday)} onChange={() => toggleWeekday(weekday)} />
                <span>{weekday.slice(0, 3)}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="holiday-time-slots">
          <label>Time slots</label>
          {timeSlots.map((slot, index) => (
            <div className="holiday-time-slot" key={`${index}-${slot.startTime}-${slot.endTime}`}>
              <input aria-label={`Start time ${index + 1}`} type="time" value={slot.startTime} onChange={(event) => updateTimeSlot(index, "startTime", event.target.value)} />
              <input aria-label={`End time ${index + 1}`} type="time" value={slot.endTime} onChange={(event) => updateTimeSlot(index, "endTime", event.target.value)} />
              <button className="danger-button" type="button" onClick={() => removeTimeSlot(index)} aria-label={`Remove time slot ${index + 1}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addTimeSlot}>
            <Plus size={16} />
            Add time slot
          </button>
        </div>
        <div className="two-cols">
          <div>
            <label>Capacity</label>
            <input type="number" min="1" value={capacity} onChange={(event) => setCapacity(event.target.value)} />
          </div>
          <div>
            <label>Override price AUD</label>
            <input type="number" min="0" step="0.01" value={priceAud} onChange={(event) => setPriceAud(event.target.value)} />
          </div>
        </div>
        <button className="primary" type="button" onClick={generateBlocks}>
          <CalendarDays size={16} />
          Generate blocks
        </button>
      </div>
      <div className="holiday-builder-section">
        <div className="split-heading">
          <div>
            <h3>Generated blocks</h3>
            <p className="muted">{blocks.length} block{blocks.length === 1 ? "" : "s"} ready to save</p>
          </div>
          <button type="button" onClick={addBlock}>
            <Plus size={16} />
            Add block
          </button>
        </div>
        <div className="generated-block-list">
          {blocks.length === 0 ? <p className="muted">No blocks yet. Generate blocks or add one manually.</p> : null}
          {blocks.map((block, index) => (
            <div className="generated-block-row" key={`${block.id}-${index}`}>
              <div className="generated-block-grid">
                <div>
                  <label>Date</label>
                  <input type="date" value={block.date} onChange={(event) => updateBlock(index, "date", event.target.value)} required />
                </div>
                <div>
                  <label>Start</label>
                  <input type="time" value={block.startTime} onChange={(event) => updateBlock(index, "startTime", event.target.value)} required />
                </div>
                <div>
                  <label>End</label>
                  <input type="time" value={block.endTime} onChange={(event) => updateBlock(index, "endTime", event.target.value)} required />
                </div>
                <div>
                  <label>Capacity</label>
                  <input type="number" min="1" value={block.capacity || ""} onChange={(event) => updateBlock(index, "capacity", event.target.value)} required />
                </div>
                <div>
                  <label>Price AUD</label>
                  <input type="number" min="0" step="0.01" value={block.priceCents ? (block.priceCents / 100).toFixed(2) : ""} onChange={(event) => updateBlock(index, "priceCents", event.target.value)} placeholder="Default" />
                </div>
              </div>
              <div className="generated-block-actions">
                <label className="open-toggle">
                  <input type="checkbox" checked={block.open} onChange={(event) => updateBlock(index, "open", event.target.checked)} />
                  Open
                </label>
                <button className="danger-button" type="button" onClick={() => removeBlock(index)}>
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function adminCollectionLabel(collection: string) {
  const labels: Record<string, string> = {
    registrations: "Registrations",
    "re-enrolments": "Re-enrolment",
    "reports-attendance": "Reports",
    venues: "Venues",
    terms: "Terms",
    "class-templates": "Class Templates",
	    classes: "Classes",
	    "holiday-programs": "Holiday Programs",
    "coach-coverages": "Coach Coverage",
	    members: "Staff",
    notifications: "Notifications",
    "payments-emails": "Payments & Emails"
  };
  return labels[collection] ?? collection;
}

function defaultReportTerm(terms: Term[]) {
  const today = formatInputDate(new Date());
  return terms.find((term) => term.startsAt <= today && term.endsAt >= today) ?? [...terms].sort((a, b) => b.startsAt.localeCompare(a.startsAt))[0];
}

function formatReportPercent(value: number | null) {
  if (value === null || Number.isNaN(value)) return "N/A";
  return `${value.toFixed(value % 1 === 0 ? 0 : 1)}%`;
}

function formatReportDate(value: string) {
  if (!value) return "";
  const parts = parseDateParts(value);
  if (!parts) return value;
  return new Date(parts.year, parts.month - 1, parts.day).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
}

function notificationClassIds(item: Record<string, unknown> | null | undefined) {
  if (!item) return [];
  if (Array.isArray(item.classIds)) return item.classIds.map(String).filter(Boolean);
  const classId = String(item.classId ?? "");
  return classId ? [classId] : [];
}

function notificationClassNames(item: Record<string, unknown> | null | undefined) {
  if (!item) return [];
  if (Array.isArray(item.classNames)) return item.classNames.map(String).filter(Boolean);
  const className = String(item.className ?? "");
  return className ? [className] : [];
}

function defaultNotificationMessage() {
  return `This announcement is for {{studentName}} in {{className}}.

Schedule: {{classSchedule}}
Venue: {{venueName}}

`;
}

function notificationAudienceType(item: Record<string, unknown> | null | undefined, classIds: string[]): NotificationAudienceType {
  const value = String(item?.audienceType ?? "");
  if (value === "single-class" || value === "multiple-classes" || value === "term") return value;
  return classIds.length > 1 ? "multiple-classes" : "single-class";
}

function notificationAudiencePayload(audienceType: NotificationAudienceType, termId: string, selectedClassIds: string[]) {
  if (audienceType === "term") {
    return termId ? { audienceType, termId } : null;
  }
  const classIds = [...new Set(selectedClassIds.filter(Boolean))];
  if (audienceType === "single-class") {
    return classIds[0] ? { audienceType, classId: classIds[0], classIds: [classIds[0]] } : null;
  }
  return classIds.length > 0 ? { audienceType, classIds } : null;
}

function notificationAudienceLabel(item: Record<string, unknown>) {
  const type = String(item.audienceType ?? "single-class");
  if (type === "term") return "Whole term";
  if (type === "multiple-classes") return `${notificationClassIds(item).length} classes`;
  return notificationClassNames(item)[0] || String(item.className ?? item.classId ?? "Single class");
}

function formatDateTime(value: string) {
  if (!value) return "date not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function adminCollectionsForRole(role?: SessionProfile["role"]) {
  if (role === "admin") return ["registrations", "re-enrolments", "reports-attendance", "payments-emails", "venues", "terms", "class-templates", "classes", "holiday-programs", "coach-coverages", "members", "notifications"];
  if (role === "finance" || role === "coach") return ["registrations"];
  return [];
}

function adminFieldValue(item: Record<string, unknown> | null | undefined, key: string) {
  const value = item?.[key];
  return value === undefined || value === null ? "" : String(value);
}

function adminOptionLabel(item: Record<string, unknown>) {
  return String(item.name || item.id || adminItemKey(item));
}

function classScheduleStartTime(item: Record<string, unknown> | null | undefined) {
  return adminFieldValue(item, "startTime") || parseClassScheduleTimes(adminFieldValue(item, "schedule")).startTime;
}

function classScheduleEndTime(item: Record<string, unknown> | null | undefined) {
  return adminFieldValue(item, "endTime") || parseClassScheduleTimes(adminFieldValue(item, "schedule")).endTime;
}

function parseClassScheduleTimes(schedule: string) {
  const match = schedule.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?\s*-\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i);
  if (!match) return { startTime: "", endTime: "" };
  return {
    startTime: toInputTime(match[1], match[2] ?? "00", match[3]),
    endTime: toInputTime(match[4], match[5] ?? "00", match[6])
  };
}

function toInputTime(hourRaw: string, minuteRaw: string, periodRaw?: string) {
  let hour = Number(hourRaw);
  const period = periodRaw?.toUpperCase();
  if (period === "PM" && hour < 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;
  return `${String(hour).padStart(2, "0")}:${minuteRaw.padStart(2, "0")}`;
}

function formatClassSchedule(startsAt: string, startTime: string, endTime: string) {
  const weekday = new Date(`${startsAt}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short" });
  return `${weekday} ${formatScheduleTime(startTime)} - ${formatScheduleTime(endTime)}`;
}

function formatScheduleTime(value: string) {
  const [hourText, minuteText = "00"] = value.split(":");
  const hour = Number(hourText);
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minuteText.padStart(2, "0")} ${period}`;
}

function classPriceAud(item: Record<string, unknown> | null | undefined) {
  const priceCents = Number(item?.priceCents ?? 0);
  return priceCents ? String((priceCents / 100).toFixed(2)) : "";
}

function templatePriceAud(item: Record<string, unknown> | null | undefined, key: string) {
  return centsToAudInput(Number(item?.[key] ?? 0));
}

function centsToAudInput(value: unknown) {
  const cents = Number(value ?? 0);
  return cents ? String((cents / 100).toFixed(2)) : "";
}

function addMinutesToTime(value: string, minutes: number) {
  if (!value) return "";
  const [hourRaw, minuteRaw = "0"] = value.split(":");
  const date = new Date(Date.UTC(2026, 0, 1, Number(hourRaw), Number(minuteRaw)));
  if (Number.isNaN(date.getTime())) return "";
  date.setUTCMinutes(date.getUTCMinutes() + minutes);
  return `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}`;
}

function holidayBlockPriceAud(item: Record<string, unknown> | null | undefined) {
  const priceCents = Number(item?.blockPriceCents ?? 0);
  return priceCents ? String((priceCents / 100).toFixed(2)) : "";
}

function holidayBlocksFromItem(item: Record<string, unknown> | null | undefined): AdminHolidayBlock[] {
  const blocks = Array.isArray(item?.blocks) ? (item.blocks as Record<string, unknown>[]) : [];
  return blocks
    .map((block, index) => ({
      id: String(block.id ?? holidayBlockId(String(block.date ?? ""), String(block.startTime ?? ""), String(block.endTime ?? ""), index)),
      date: String(block.date ?? ""),
      startTime: String(block.startTime ?? ""),
      endTime: String(block.endTime ?? ""),
      capacity: Number(block.capacity ?? 0),
      ...(block.priceCents === undefined || block.priceCents === null ? {} : { priceCents: Number(block.priceCents) }),
      open: block.open === undefined ? true : Boolean(block.open)
    }))
    .sort((a, b) => `${a.date} ${a.startTime}`.localeCompare(`${b.date} ${b.startTime}`));
}

function holidayTimeSlotsFromBlocks(blocks: AdminHolidayBlock[]): HolidayTimeSlot[] {
  const slots = blocks.reduce<HolidayTimeSlot[]>((current, block) => {
    if (!block.startTime || !block.endTime) return current;
    const exists = current.some((slot) => slot.startTime === block.startTime && slot.endTime === block.endTime);
    return exists ? current : [...current, { startTime: block.startTime, endTime: block.endTime }];
  }, []);
  return slots.length > 0 ? slots : [{ startTime: "10:00", endTime: "13:00" }, { startTime: "14:00", endTime: "17:00" }];
}

function holidayDatesInRange(start: string, end: string) {
  const startParts = parseDateParts(start);
  const endParts = parseDateParts(end);
  if (!startParts || !endParts) return [];
  const startDate = new Date(startParts.year, startParts.month - 1, startParts.day);
  const endDate = new Date(endParts.year, endParts.month - 1, endParts.day);
  if (startDate > endDate) return [];
  const dates: string[] = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    dates.push(formatInputDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

function weekdayForDate(date: string) {
  const parts = parseDateParts(date);
  if (!parts) return "";
  const value = new Date(parts.year, parts.month - 1, parts.day);
  return weekdays[value.getDay() === 0 ? 6 : value.getDay() - 1];
}

function parseDateParts(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3])
  };
}

function formatInputDate(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function holidayBlockId(date: string, startTime: string, endTime: string, index: number) {
  return `${date}-${startTime}-${endTime}-${index}`.replace(/[^a-zA-Z0-9-]/g, "-");
}

function withoutPriceCents(block: AdminHolidayBlock): AdminHolidayBlock {
  const { priceCents: _priceCents, ...rest } = block;
  return rest;
}

function parseHolidayBlocksJson(value: string): AdminHolidayBlock[] {
  const parsed = value ? JSON.parse(value) : [];
  if (!Array.isArray(parsed) || parsed.length === 0) throw new Error("Add at least one holiday program block.");
  return parsed.map((rawBlock, index) => {
    const block = rawBlock as Partial<AdminHolidayBlock>;
    const date = String(block.date ?? "");
    const startTime = String(block.startTime ?? "");
    const endTime = String(block.endTime ?? "");
    if (!date || !startTime || !endTime) throw new Error(`Block ${index + 1} needs date, start time and end time.`);
    const capacity = Number(block.capacity ?? 0);
    if (!capacity || capacity < 1) throw new Error(`Block ${index + 1} needs capacity.`);
    const priceCents = block.priceCents === undefined || block.priceCents === null ? undefined : Number(block.priceCents);
    if (priceCents !== undefined && (!Number.isFinite(priceCents) || priceCents < 0)) throw new Error(`Block ${index + 1} has an invalid price.`);
    return {
      id: String(block.id ?? holidayBlockId(date, startTime, endTime, index)),
      date,
      startTime,
      endTime,
      capacity,
      ...(priceCents === undefined ? {} : { priceCents }),
      open: block.open === undefined ? true : Boolean(block.open)
    };
  });
}

function holidayBlockDateRange(blocks: AdminHolidayBlock[]) {
  const dates = blocks.map((block) => block.date).filter(Boolean).sort();
  return {
    startsAt: dates[0] ?? "",
    endsAt: dates[dates.length - 1] ?? ""
  };
}

function adminItemKey(item: Record<string, unknown>) {
  return String(item.id ?? item.userKey ?? item.phone ?? item.email ?? item.subject ?? item.name ?? "");
}

function adminItemTitle(collection: string, item: Record<string, unknown>) {
  if (collection === "members") return String(item.name || item.email || item.phone || "Staff member");
  if (collection === "class-templates") return String(item.name || "Class template");
  if (collection === "coach-coverages") return String(item.targetName || item.classId || item.programId || "Coach coverage");
  if (collection === "notifications") return String(item.subject || "Notification");
  return String(item.name || item.id || "Untitled item");
}

function adminListItems(collection: string, items: Record<string, unknown>[]) {
  if (collection === "class-templates") {
    return [...items].sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0) || String(a.name ?? "").localeCompare(String(b.name ?? "")));
  }
  return items;
}

function adminItemSubtitle(collection: string, item: Record<string, unknown>, classTemplates: ClassTemplate[] = []) {
  if (collection === "venues") return String(item.address || "No address");
  if (collection === "terms") return `${String(item.startsAt || "")} - ${String(item.endsAt || "")}`;
  if (collection === "class-templates") return classTemplateSubtitle(item, classTemplates);
	  if (collection === "classes") return `${String(item.schedule || "No schedule")} · ${formatAud(Number(item.priceCents ?? 0))}`;
	  if (collection === "holiday-programs") return `${String(item.startsAt || "")} - ${String(item.endsAt || "")} · ${formatAud(Number(item.blockPriceCents ?? 0))} per block`;
  if (collection === "coach-coverages") {
    return `${String(item.date || item.blockLabel || "Date not set")} · ${String(item.coachName || item.coachPhone || "No coach")}${item.note ? ` · ${String(item.note)}` : ""}`;
  }
  if (collection === "members") return `${String(item.role || "staff")} · ${String(item.email || item.phone || "")}`;
  if (collection === "notifications") return `${notificationAudienceLabel(item)} · sent ${String(item.emailCount ?? item.recipientCount ?? item.sentCount ?? 0)} email${Number(item.emailCount ?? item.recipientCount ?? item.sentCount ?? 0) === 1 ? "" : "s"}`;
  return adminItemKey(item);
}

function classTemplateSubtitle(item: Record<string, unknown>, classTemplates: ClassTemplate[]) {
  const nextTemplateId = String(item.nextTemplateId ?? "");
  const nextTemplate = classTemplates.find((template) => template.id === nextTemplateId);
  const nextLabel = nextTemplateId ? `next ${nextTemplate?.name ?? "template not found"}` : "no automatic progression";
  return `${String(item.level || "No level")} · ${String(item.ageGroup || "No age group")} · ${nextLabel}`;
}

function normalizeAdminPayload(collection: string, data: Record<string, FormDataEntryValue>) {
  const payload: Record<string, unknown> = {};
  Object.entries(data).forEach(([key, value]) => {
    payload[key] = String(value);
  });
	  if (collection === "classes") {
	    payload.capacity = Number(payload.capacity);
	    payload.priceCents = Math.round(Number(payload.priceAud) * 100);
	    payload.schedule = formatClassSchedule(String(payload.startsAt), String(payload.startTime), String(payload.endTime));
	    delete payload.priceAud;
	  }
  if (collection === "class-templates") {
    payload.defaultCapacity = Number(payload.defaultCapacity || 0);
    payload.defaultDurationMinutes = Number(payload.defaultDurationMinutes || 60);
    payload.defaultPriceCents = Math.round(Number(payload.defaultPriceAud || 0) * 100);
    payload.sortOrder = Number(payload.sortOrder || 0);
    payload.active = data.active === "true";
    delete payload.defaultPriceAud;
  }
	  if (collection === "holiday-programs") {
	    const blocks = parseHolidayBlocksJson(String(payload.blocksJson ?? ""));
	    const dateRange = holidayBlockDateRange(blocks);
	    payload.blockPriceCents = Math.round(Number(payload.blockPriceAud) * 100);
	    payload.blocks = blocks;
	    payload.startsAt = dateRange.startsAt;
	    payload.endsAt = dateRange.endsAt;
	    delete payload.blockPriceAud;
	    delete payload.blocksJson;
	  }
  if (collection === "coach-coverages") {
    if (payload.targetType === "class") {
      delete payload.programId;
      delete payload.blockId;
    }
    if (payload.targetType === "holiday") {
      delete payload.classId;
    }
  }
	  return payload;
	}
